import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TableSortLabel,
  Tooltip,
  Tabs,
  Tab,
  Divider,
  InputAdornment,
  FormControlLabel,
  Checkbox,
  Switch,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  useTheme,
} from '@mui/material';
import {
  Remove as RemoveIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ArchiveOutlined as ArchiveIcon,
  SnoozeOutlined as SnoozeIcon,
  RequestQuoteOutlined as RequestQuoteIcon,
  DevicesOtherOutlined as DevicesIcon,
  ArrowDropUp as ArrowUpIcon,
} from '@mui/icons-material';
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Tooltip as RechartsTooltip,
} from 'recharts';
import ListSubheader from '@mui/material/ListSubheader';
import { MapContainer, TileLayer, Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import StatsCard from '../components/StatsCard';
import {
  fetchLicenses,
  fetchLicenseBilling,
  fetchLicenseHistory,
  fetchRtkPremiumLicenses,
  updateRtkPremiumLabel,
  decommissionRtkLicense,
  decommissionPointOneLicense,
  fetchGPSData,
  fetchGpsSystems,
  assignLicenseToGps,
  updateLicenseName,
  fetchNextLicenseNumber,
  bulkAssignLicenseNumbers,
  fetchSurveyGps,
  assignLicenseToRover,
  fetchRegions,
  fetchBusinessUnits,
  fetchBusinessUnitForRegion,
  fetchAvailableCount,
  fetchLicenseCheckouts,
  checkoutLicense,
  provisionPointOne,
  provisionRtkPremium,
  extendLicenseCheckout,
  returnLicenseCheckout,
  dismissCheckout,
  locationCheck,
  fetchCoverage,
} from '../services/api';

const RTKPREMIUM_PLANS = [
  { id: 'mXKL', label: '1 Month' },
  { id: '8NwQ', label: '3 Months' },
  { id: 'm6jb', label: '12 Months' },
];
import { useAuth, canEditEquipment } from '../context/AuthContext';
import { formatExpiry as _formatExpiryRaw, formatExpiryWithTime, daysUntil, hoursUntil } from '../utils/time';

function getRelativeTime(timestamp) {
  if (!timestamp) return 'Never Active';
  const now = new Date();
  // DB timestamps are UTC but P1N rows arrive without a timezone marker
  const iso = /Z|[+-]\d{2}:\d{2}$/.test(timestamp) ? timestamp : timestamp + 'Z';
  const then = new Date(iso);
  if (isNaN(then.getTime())) return 'Never Active';
  const diffMs = now - then;
  if (diffMs < 0) return 'Just now';
  const minutes = Math.floor(diffMs / 60000);
  const hours = Math.floor(diffMs / 3600000);
  const days = Math.floor(diffMs / 86400000);
  const months = Math.floor(days / 30);
  if (minutes < 1) return 'Just now';
  if (minutes < 60) return `${minutes} min`;
  if (hours < 24) return `${hours} hr`;
  if (days < 30) return `${days} day${days > 1 ? 's' : ''}`;
  if (months < 12) return `${months} month${months > 1 ? 's' : ''}`;
  return `${Math.floor(months / 12)} yr`;
}

function getHealthColor(connectionStatus) {
  switch (connectionStatus) {
    case 'GOOD': return '#00ff41';
    case 'NO_CONNECTION': return '#ff1a1a';
    case 'NO_POSITION': return '#ff8c00';
    default: return '#ff8c00';
  }
}

function getHealthLabel(connectionStatus) {
  switch (connectionStatus) {
    case 'GOOD': return 'Good';
    case 'NO_CONNECTION': return 'No Connection';
    case 'NO_POSITION': return 'No Position';
    default: return 'Unknown';
  }
}

function StatusDot({ status }) {
  return (
    <Tooltip title={getHealthLabel(status)} arrow>
      <Box sx={{
        width: 32, height: 32, borderRadius: '50%',
        background: `radial-gradient(circle, ${getHealthColor(status)} 45%, ${getHealthColor(status)}88 55%, ${getHealthColor(status)}22 70%, transparent 80%)`,
        flexShrink: 0,
      }} />
    </Tooltip>
  );
}

// ─── Credentials Banner ───────────────────────────────────────────────────────
function CredentialsBanner({ checkouts, onReturn, onExtend, formatExpiry }) {
  const [visible, setVisible] = useState({});
  const [dismissing, setDismissing] = useState(new Set());
  const [collapsing, setCollapsing] = useState(new Set());

  const toggleVisible = (id) => setVisible(v => ({ ...v, [id]: !v[id] }));
  const copy = (text) => navigator.clipboard.writeText(text);

  const animateOut = (id, apiCall) => {
    setDismissing(prev => new Set([...prev, id]));
    setTimeout(() => setCollapsing(prev => new Set([...prev, id])), 400);
    setTimeout(async () => {
      try { await apiCall(); onReturn(); } catch {}
    }, 700);
  };

  const visibleCheckouts = checkouts.filter(c => {
    const days = daysUntil(c.expires_at);
    return days === null || days >= -7;
  });

  if (!visibleCheckouts.length) return null;

  return (
    <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column' }}>
      {visibleCheckouts.map((c) => {
        const days = daysUntil(c.expires_at);
        const hours = hoursUntil(c.expires_at);
        const noEndDate = !c.expires_at;
        const isExpired = days !== null && days <= 0;
        const underDay = !isExpired && hours !== null && hours <= 24;
        const borderColor = isExpired ? '#ff1a1a' : noEndDate ? 'divider' : days <= 7 ? '#ff8c00' : 'divider';
        const isWarning = isExpired || (!noEndDate && days <= 7);
        return (
          <Box
            key={c.id}
            sx={{
              overflow: 'hidden',
              transition: 'max-height 0.3s ease, opacity 0.4s ease, transform 0.4s ease',
              maxHeight: collapsing.has(c.id) ? 0 : 600,
              opacity: dismissing.has(c.id) ? 0 : 1,
              transform: dismissing.has(c.id) ? 'translateY(-8px)' : 'translateY(0)',
            }}
          >
          <Paper
            sx={{
              p: 2.5,
              border: isWarning ? '3px solid' : '1px solid',
              borderColor,
              borderRadius: 2,
              mb: 2,
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={700} sx={{ color: isExpired ? '#ff1a1a' : '#00c853' }}>
                  {isExpired ? 'Expired License' : 'Active License'}
                </Typography>
                {c.assigned_to && (
                  <Typography variant="body2" color="text.secondary">
                    <strong>{c.assigned_to}</strong>
                  </Typography>
                )}
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                {isExpired && (
                  <>
                    <Typography variant="body2" fontWeight={600} sx={{ color: '#ff1a1a', mr: 1 }}>
                      {formatExpiry(c.expires_at)}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={dismissing.has(c.id)}
                      onClick={() => animateOut(c.id, () => dismissCheckout(c.id))}
                    >
                      Dismiss
                    </Button>
                  </>
                )}
                {!isExpired && (
                  <>
                    {c.auto_renew ? (
                      <Typography variant="body2" sx={{ color: '#00c853', fontWeight: 600, mr: 1 }}>
                        Auto-renew
                      </Typography>
                    ) : (
                      <Typography variant="body2" sx={{ color: noEndDate ? 'text.secondary' : underDay ? '#ff8c00' : days <= 7 ? '#ff8c00' : 'text.secondary', mr: 1 }}>
                        {noEndDate
                          ? 'No End Date'
                          : underDay
                          ? hours <= 1 ? 'Less than one hour' : `Expires in ${hours} hours`
                          : `Expires ${formatExpiry(c.expires_at)}${days <= 7 ? ` (${days} day${days !== 1 ? 's' : ''})` : ''}`}
                      </Typography>
                    )}
                    {!c.auto_renew && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onExtend(c)}
                      >
                        Extend
                      </Button>
                    )}
                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      disabled={dismissing.has(c.id)}
                      onClick={() => animateOut(c.id, () => returnLicenseCheckout(c.id))}
                      sx={{ borderRadius: 1 }}
                    >
                      Return
                    </Button>
                  </>
                )}
              </Box>
            </Box>
            <Grid container spacing={1.5}>
              {[
                { label: 'Address', value: c.ntrip_caster_url || (c.provider === 'rtkpremium' ? 'rtkpremium.xyz' : 'truertk.pointonenav.com') },
                { label: 'Port', value: '2101' },
                { label: 'Mountpoint', value: c.ntrip_mount_point || (c.provider === 'rtkpremium' ? 'COMMON' : 'POLARIS_LOCAL') },
                { label: 'Username', value: c.ntrip_login || '—' },
                { label: 'Password', value: c.ntrip_password || '—' },
              ].map(({ label, value, secret }) => (
                <Grid item xs={12} sm={6} md={4} key={label}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                      <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                        {secret && !visible[`${c.id}-${label}`] ? '••••••••' : value}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexShrink: 0 }}>
                      {secret && (
                        <IconButton size="small" onClick={() => toggleVisible(`${c.id}-${label}`)} sx={{ opacity: 0.6 }}>
                          {visible[`${c.id}-${label}`] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                        </IconButton>
                      )}
                      <IconButton size="small" onClick={() => copy(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                        <CopyIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Paper>
          </Box>
        );
      })}
    </Box>
  );
}

// Provider colours, shared by the reporting table rows and their phone cards.
const COLOR_P1N = '#00c853';
const COLOR_RTK = '#4a90e2';

// Phone layout for the Reporting tables: one card per row with the same fields,
// matching the card rows the Rover/Machine Licenses tabs use below md. The real
// table still renders from md up.
function ReportRowCard({ name, detail, type, region, provider, days, extra }) {
  return (
    <Card sx={{ mb: 0.75, border: 1, borderColor: 'divider' }}>
      <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
        <Grid container spacing={1} alignItems="flex-start">
          <Grid item xs={8}>
            <Typography variant="body2" fontWeight={700}>{name}</Typography>
            {detail && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{detail}</Typography>
            )}
          </Grid>
          <Grid item xs={4} sx={{ textAlign: 'right' }}>{days}</Grid>
          <Grid item xs={6}>
            <Box sx={{
              display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600,
              backgroundColor: type === 'Rover' ? 'rgba(74,144,226,0.15)' : 'rgba(0,200,83,0.15)',
              color: type === 'Rover' ? COLOR_RTK : COLOR_P1N,
            }}>{type}</Box>
          </Grid>
          <Grid item xs={6}>
            <Typography variant="body2" sx={{ textAlign: 'right' }}>{region || '—'}</Typography>
          </Grid>
          <Grid item xs={12}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: provider === 'pointone' ? COLOR_P1N : COLOR_RTK }} />
              <Typography variant="caption" color="text.secondary">
                {provider === 'pointone' ? 'PointOneNav' : 'Premium RTK'}
              </Typography>
              {extra}
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
}

// ─── Coverage Map ─────────────────────────────────────────────────────────────
// Each provider keeps its own colour when shown alone; the combined "both" view
// uses a distinct third colour so the merged footprint reads as one thing.
// Merged 'Both' footprint is magenta: distinct from each provider's own colour
// and, unlike the previous orange, still separable from the Point One green for
// red-green colour blindness (Ali's pick, 2026-09-17).
const COVERAGE_COLORS = { pointone: '#00c853', premium: '#2979ff', both: '#d81b60' };
const COVERAGE_PANE = 'coverage';
// A provider whose nearest point is farther than this is treated as out of range.
const COVERAGE_MAX_RANGE_KM = 1000;

// Initial map view per account country; falls back to a US-centred continental view.
const COVERAGE_DEFAULT_VIEW = { center: [43, -96], zoom: 4 };
const COVERAGE_COUNTRY_VIEW = {
  'United States': { center: [39.5, -98.35], zoom: 4 },
  'Canada': { center: [56.13, -106.35], zoom: 3 },
  'France': { center: [46.6, 2.2], zoom: 5 },
  'United Kingdom': { center: [54.5, -3.0], zoom: 5 },
};
// Both providers' circles are drawn fully opaque into ONE canvas/pane (so same-
// colour overlaps merge with no darker seams, across providers too), then the
// whole pane is faded to this single opacity. While a search pin is shown the fill
// is dimmed further so it doesn't wash out the pin, lines, and info box.
const MERGED_COVERAGE_OPACITY = 0.4;
const DIMMED_COVERAGE_OPACITY = 0.12;
// Space kept clear under the map so its bottom edge sits just above the fixed
// footer: MainLayout's FOOTER_HEIGHT (56) plus the main area's bottom padding (24).
const COVERAGE_BOTTOM_CLEARANCE = 56 + 24;
const COVERAGE_MIN_HEIGHT = 360;

// Leaflet only re-measures on window resize, so a container that changes height
// (the fit-to-screen sizing below) would leave grey untiled strips. Watch the
// container itself and invalidate on any size change.
function InvalidateOnResize() {
  const map = useMap();
  useEffect(() => {
    const el = map.getContainer();
    const ro = new ResizeObserver(() => map.invalidateSize());
    ro.observe(el);
    return () => ro.disconnect();
  }, [map]);
  return null;
}

// Both correction services cover ~50 km per point: P1N per base station, Premium
// per grid point (the backend counts a spot covered if within 50 km of any). We
// draw a 50 km circle per point and let them merge into one flat region. The raw
// Premium grid is ~14k points at ~1 km spacing — absurdly redundant for a 50 km
// radius — so it's thinned to ~0.15° (~15 km) cells first; the union is unchanged
// but we draw a fraction of the circles. Everything goes into one canvas so the
// two providers merge seamlessly. Imperative so the circles aren't React nodes.
function CoverageLayer({ premiumPoints, stations, radiusM, color, showPremium, showP1, dimmed }) {
  const map = useMap();
  useEffect(() => {
    let pane = map.getPane(COVERAGE_PANE);
    if (!pane) {
      pane = map.createPane(COVERAGE_PANE);
      pane.style.zIndex = 410; // above tiles/overlay, below markers & popups
    }
    pane.style.opacity = String(dimmed ? DIMMED_COVERAGE_OPACITY : MERGED_COVERAGE_OPACITY);
    const renderer = L.canvas({ pane: COVERAGE_PANE, padding: 0.5 });
    const group = L.layerGroup();
    const addCircle = (lat, lon, popupHtml) => {
      const circle = L.circle([lat, lon], {
        renderer,
        radius: radiusM,
        stroke: false,
        fillColor: color,
        fillOpacity: 1,
        interactive: !!popupHtml,
      });
      if (popupHtml) circle.bindPopup(popupHtml);
      circle.addTo(group);
    };

    if (showPremium && premiumPoints?.length) {
      const seen = new Set();
      for (let i = 0; i < premiumPoints.length; i++) {
        const lat = premiumPoints[i][0], lon = premiumPoints[i][1];
        const key = `${Math.round(lat / 0.15)}_${Math.round(lon / 0.15)}`;
        if (!seen.has(key)) { seen.add(key); addCircle(lat, lon, null); }
      }
    }
    if (showP1 && stations?.length) {
      const fmtUptime = (u) => (u == null ? '' : `<br/>30-day uptime: ${(u <= 1 ? u * 100 : u).toFixed(1)}%`);
      for (const s of stations) {
        const name = (s.name || 'Station').replace(/</g, '&lt;');
        addCircle(s.lat, s.lon, `<strong>${name}</strong><br/>Point One base station${fmtUptime(s.uptime_30d)}`);
      }
    }
    group.addTo(map);
    return () => { map.removeLayer(group); renderer.remove(); };
  }, [premiumPoints, stations, radiusM, color, showPremium, showP1, dimmed, map]);
  return null;
}

// A small solid dot at every station/point, colour-coded by provider (green =
// Point One, blue = Premium), drawn on its own canvas pane above the coverage
// fill. Non-interactive so it doesn't intercept the coverage popups, and canvas-
// rendered so the ~18k dots stay fast.
const STATION_DOTS_PANE = 'stationDots';
function StationDots({ premiumPoints, stations, showPremium, showP1 }) {
  const map = useMap();
  useEffect(() => {
    let pane = map.getPane(STATION_DOTS_PANE);
    if (!pane) {
      pane = map.createPane(STATION_DOTS_PANE);
      pane.style.zIndex = 415; // above the coverage fill (410), below markers/popups
    }
    const renderer = L.canvas({ pane: STATION_DOTS_PANE, padding: 0.5 });
    const group = L.layerGroup();
    // Dots grow as the map zooms in: 1px at the country-level views up to 4px
    // fully zoomed, so they stay subtle from afar (and peek out only slightly
    // beneath the provider pin on a search result).
    const radiusForZoom = () => Math.min(4, Math.max(1, 1 + (map.getZoom() - 4) * 0.4));
    let radius = radiusForZoom();
    const addDot = (lat, lon, color) => {
      L.circleMarker([lat, lon], {
        renderer,
        radius,
        stroke: false,
        fillColor: color,
        fillOpacity: 0.95,
        interactive: false,
      }).addTo(group);
    };
    if (showPremium && premiumPoints?.length) {
      for (let i = 0; i < premiumPoints.length; i++) {
        addDot(premiumPoints[i][0], premiumPoints[i][1], COVERAGE_COLORS.premium);
      }
    }
    if (showP1 && stations?.length) {
      for (const s of stations) addDot(s.lat, s.lon, COVERAGE_COLORS.pointone);
    }
    // Resize in place on zoom — setRadius batches into one canvas repaint, so the
    // ~18k dots aren't torn down and rebuilt.
    const onZoom = () => {
      const r = radiusForZoom();
      if (r === radius) return;
      radius = r;
      group.eachLayer((dot) => dot.setRadius(r));
    };
    map.on('zoomend', onZoom);
    group.addTo(map);
    return () => { map.off('zoomend', onZoom); map.removeLayer(group); renderer.remove(); };
  }, [premiumPoints, stations, showPremium, showP1, map]);
  return null;
}

// Great-circle distance in km.
function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const dLat = ((lat2 - lat1) * Math.PI) / 180;
  const dLon = ((lon2 - lon1) * Math.PI) / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos((lat1 * Math.PI) / 180) * Math.cos((lat2 * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(a));
}

// Nearest item to (lat, lon). getLat/getLon read coords off each item. Returns
// { km, lat, lon, item } for the closest, or null for an empty list.
function nearestFrom(lat, lon, items, getLat, getLon) {
  let best = null;
  for (let i = 0; i < items.length; i++) {
    const iLat = getLat(items[i]), iLon = getLon(items[i]);
    const km = haversineKm(lat, lon, iLat, iLon);
    if (!best || km < best.km) best = { km, lat: iLat, lon: iLon, item: items[i] };
  }
  return best;
}

// Parse "lat, lon" / "lat lon" decimal pairs. Returns [lat, lon] or null.
function parseLatLon(text) {
  const m = text.trim().match(/^(-?\d+(?:\.\d+)?)\s*[, ]\s*(-?\d+(?:\.\d+)?)$/);
  if (!m) return null;
  const lat = parseFloat(m[1]), lon = parseFloat(m[2]);
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return null;
  return [lat, lon];
}

// Teardrop pin for the searched location itself.
const makePinIcon = (fill, scale = 1) => {
  const w = Math.round(17 * scale), h = Math.round(26 * scale);
  return L.divIcon({
    className: 'coverage-search-pin',
    html: `<svg width="${w}" height="${h}" viewBox="0 0 24 36" xmlns="http://www.w3.org/2000/svg"><path d="M12 0C5.37 0 0 5.37 0 12c0 8.4 12 24 12 24s12-15.6 12-24C24 5.37 18.63 0 12 0z" fill="${fill}"/><circle cx="12" cy="12" r="4.5" fill="#fff"/></svg>`,
    iconSize: [w, h],
    iconAnchor: [Math.round(w / 2), h],
    popupAnchor: [0, -Math.round(23 * scale)],
  });
};

// Station glyph (antenna mast with radiating arcs) for the nearest-provider
// pins and the result-card legend. "station-minimalistic" bold from the Solar
// icon set by 480 Design — CC BY 4.0, via Iconify.
const STATION_PATHS = '<path d="M12 2.75c-3.446 0-6.25 2.817-6.25 6.305a6.3 6.3 0 0 0 1.795 4.421a.75.75 0 0 1-1.074 1.048a7.8 7.8 0 0 1-2.221-5.47C4.25 4.75 7.714 1.25 12 1.25s7.75 3.5 7.75 7.805a7.8 7.8 0 0 1-2.186 5.433a.75.75 0 1 1-1.08-1.04a6.3 6.3 0 0 0 1.766-4.393c0-3.488-2.804-6.305-6.25-6.305"/><path d="M12 5.572c-1.9 0-3.45 1.554-3.45 3.483c0 .946.372 1.801.978 2.429a.75.75 0 0 1-1.08 1.041a5 5 0 0 1-1.398-3.47c0-2.747 2.21-4.983 4.95-4.983s4.95 2.236 4.95 4.983a5 5 0 0 1-1.361 3.432a.75.75 0 1 1-1.091-1.03c.59-.625.952-1.47.952-2.402c0-1.93-1.55-3.483-3.45-3.483"/><path fill-rule="evenodd" d="M11.284 9.775A1.249 1.249 0 0 1 12 7.5a1.25 1.25 0 0 1 .716 2.275l2.49 7.471l.011.033l1.495 4.484a.75.75 0 0 1-1.423.474l-1.33-3.987h-3.918l-1.33 3.987a.75.75 0 0 1-1.423-.474l1.495-4.484l.011-.033zM12 12.372l1.46 4.378h-2.92z" clip-rule="evenodd"/>';
// Provider station pin: green = Point One, blue = Premium RTK (CM9). White
// outline + drop shadow keep it legible over the coverage fill in both themes.
const makeStationIcon = (fill, scale = 1) => {
  const s = Math.round(26 * scale);
  return L.divIcon({
    className: 'coverage-search-pin',
    html: `<svg width="${s}" height="${s}" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" style="filter:drop-shadow(0 1px 2px rgba(0,0,0,0.55))"><g fill="${fill}" stroke="#fff" stroke-width="0.9" paint-order="stroke">${STATION_PATHS}</g></svg>`,
    iconSize: [s, s],
    iconAnchor: [Math.round(s / 2), s],
    popupAnchor: [0, -Math.round(22 * scale)],
  });
};
// Pins keep their base size out to the default search framing, then grow with
// zoom up to 2x so they don't vanish against the map when zoomed way in.
const pinScaleForZoom = (z) => Math.min(2, Math.max(1, 1 + (z - 6) * 0.12));

// The unit system chosen in the settings menu (MainLayout owns the toggle and
// fires 'gps-units-changed' whenever it flips).
function useUnits() {
  const [units, setUnits] = useState(() => localStorage.getItem('gps-units') || 'imperial');
  useEffect(() => {
    const onChange = () => setUnits(localStorage.getItem('gps-units') || 'imperial');
    window.addEventListener('gps-units-changed', onChange);
    return () => window.removeEventListener('gps-units-changed', onChange);
  }, []);
  return units;
}

// Distance formatting for the search card and station popups.
const fmtDistance = (km, units) => {
  if (units === 'metric') return km >= 100 ? `${Math.round(km)} km` : `${km.toFixed(1)} km`;
  const mi = km * 0.621371;
  return mi >= 100 ? `${Math.round(mi)} mi` : `${mi.toFixed(1)} mi`;
};

// Estimated RTK accuracy at a given baseline distance, linear fit from the
// provider chart: horizontal 0.6→3.1 cm and vertical 1.0→6.0 cm over 0→50 km.
const fmtAccuracy = (km, units) => {
  const h = 0.6 + 0.05 * km, v = 1.0 + 0.1 * km;
  return units === 'metric'
    ? `±${h.toFixed(1)} cm H · ±${v.toFixed(1)} cm V`
    : `±${(h / 2.54).toFixed(1)} in H · ±${(v / 2.54).toFixed(1)} in V`;
};

// Candidate anchors for the search result card, in preference order. Bottom-left
// is the default; the top-left slot sits right of the zoom control, and top-right
// is never used (Station Pins toggle lives there).
const CARD_CORNERS = {
  'bottom-left': { bottom: 12, left: 12 },
  'bottom-right': { bottom: 12, right: 12 },
  'top-left': { top: 12, left: 56 },
};

// Re-anchors the search card whenever it would cover a pin: projects the pins to
// screen space on every move/zoom and reports the first corner whose rectangle
// clears them all (falling back to bottom-left).
function CardCornerPicker({ result, cardEl, onCorner }) {
  const map = useMap();
  useEffect(() => {
    if (!result) { onCorner('bottom-left'); return undefined; }
    const compute = () => {
      const size = map.getSize();
      const s = pinScaleForZoom(map.getZoom());
      // Cover both marker shapes: teardrop search pin (20x30) and provider station (26x26)
      const pinW = 26 * s, pinH = 30 * s;
      const pinRects = [
        [result.lat, result.lon],
        result.pointone && [result.pointone.lat, result.pointone.lon],
        result.premium && [result.premium.lat, result.premium.lon],
      ].filter(Boolean).map((ll) => {
        const p = map.latLngToContainerPoint(ll);
        return { left: p.x - pinW / 2, right: p.x + pinW / 2, top: p.y - pinH, bottom: p.y };
      });
      const cardW = cardEl?.offsetWidth || 300, cardH = cardEl?.offsetHeight || 90;
      const M = 12;
      const candidates = {
        'bottom-left': { left: M, top: size.y - M - cardH },
        'bottom-right': { left: size.x - M - cardW, top: size.y - M - cardH },
        'top-left': { left: CARD_CORNERS['top-left'].left, top: M },
      };
      const clear = Object.entries(candidates).find(([, c]) =>
        !pinRects.some((r) => c.left < r.right && c.left + cardW > r.left && c.top < r.bottom && c.top + cardH > r.top)
      );
      onCorner(clear ? clear[0] : 'bottom-left');
    };
    compute();
    map.on('moveend zoomend', compute);
    return () => map.off('moveend zoomend', compute);
  }, [map, result, cardEl, onCorner]);
  return null;
}

// Renders the search pin and a dashed connector line to each in-range provider's
// nearest point. Lines live in a pane above the coverage fill so they aren't
// hidden under it. The result details themselves live in a card fixed to the
// map's bottom-left corner (rendered by the tab, not here) so they never cover
// the pins.
function SearchResult({ result }) {
  const map = useMap();
  const units = useUnits();
  const pointoneRef = useRef(null);
  const premiumRef = useRef(null);
  if (!map.getPane('searchLines')) {
    map.createPane('searchLines').style.zIndex = 420;
  }
  // Track the zoom level so the pin icons regenerate at their zoom-scaled size.
  // Swapping a marker's icon closes its open popup (Leaflet re-binds it), so
  // remember which popup was open at zoomend and reopen it after the swap.
  const [zoom, setZoom] = useState(() => map.getZoom());
  const reopenRef = useRef(null);
  useEffect(() => {
    const onZoom = () => {
      reopenRef.current =
        pointoneRef.current?.isPopupOpen() ? pointoneRef
        : premiumRef.current?.isPopupOpen() ? premiumRef
        : null;
      setZoom(map.getZoom());
    };
    map.on('zoomend', onZoom);
    return () => { map.off('zoomend', onZoom); };
  }, [map]);
  const pinScale = pinScaleForZoom(zoom);
  const icons = useMemo(() => ({
    search: makePinIcon('#ff1744', pinScale),
    pointone: makeStationIcon(COVERAGE_COLORS.pointone, pinScale),
    premium: makeStationIcon(COVERAGE_COLORS.premium, pinScale),
  }), [pinScale]);
  useEffect(() => {
    const reopen = reopenRef.current;
    reopenRef.current = null;
    reopen?.current?.openPopup();
  }, [icons]);
  useEffect(() => {
    if (!result) return;
    const pts = [[result.lat, result.lon]];
    if (result.pointone) pts.push([result.pointone.lat, result.pointone.lon]);
    if (result.premium) pts.push([result.premium.lat, result.premium.lon]);
    if (pts.length > 1) map.fitBounds(pts, { padding: [70, 70], maxZoom: 10 });
    else map.setView([result.lat, result.lon], 8);
  }, [result, map]);
  if (!result) return null;
  return (
    <>
      {result.pointone && (
        <Polyline
          pane="searchLines"
          positions={[[result.lat, result.lon], [result.pointone.lat, result.pointone.lon]]}
          pathOptions={{ color: COVERAGE_COLORS.pointone, weight: 3, dashArray: '6 7' }}
        />
      )}
      {result.premium && (
        <Polyline
          pane="searchLines"
          positions={[[result.lat, result.lon], [result.premium.lat, result.premium.lon]]}
          pathOptions={{ color: COVERAGE_COLORS.premium, weight: 3, dashArray: '6 7' }}
        />
      )}
      {result.pointone && (
        <Marker position={[result.pointone.lat, result.pointone.lon]} icon={icons.pointone} ref={pointoneRef}>
          <Popup>
            <div style={{ minWidth: 130 }}>
              <strong>Point One Base Station</strong>
              {result.pointone.name ? <><br />{result.pointone.name}</> : null}
              <br />{fmtDistance(result.pointone.km, units)} Away
              <br />Est. accuracy: {fmtAccuracy(result.pointone.km, units)}
            </div>
          </Popup>
        </Marker>
      )}
      {result.premium && (
        <Marker position={[result.premium.lat, result.premium.lon]} icon={icons.premium} ref={premiumRef}>
          <Popup>
            <div style={{ minWidth: 130 }}>
              <strong>Premium Coverage Point</strong>
              <br />{fmtDistance(result.premium.km, units)} Away
              <br />Est. accuracy: {fmtAccuracy(result.premium.km, units)}
            </div>
          </Popup>
        </Marker>
      )}
      <Marker position={[result.lat, result.lon]} icon={icons.search} />
    </>
  );
}

function CoverageMap({ coverage, layer, isDark, searchResult, country, showStationDots, cardEl, onCardCorner }) {
  const showP1 = layer === 'both' || layer === 'pointone';
  const showPremium = layer === 'both' || layer === 'premium';
  const stations = coverage?.pointone || [];
  const premium = coverage?.premium || [];
  const radiusM = (coverage?.pointone_radius_km || 50) * 1000;
  // Each provider keeps its own colour alone; "both" uses the distinct merged colour.
  const color = layer === 'both' ? COVERAGE_COLORS.both
    : layer === 'pointone' ? COVERAGE_COLORS.pointone
    : COVERAGE_COLORS.premium;
  // Frame the account's country on first open; the search's fitBounds takes over after.
  const view = COVERAGE_COUNTRY_VIEW[country] || COVERAGE_DEFAULT_VIEW;
  return (
    <MapContainer
      center={view.center}
      zoom={view.zoom}
      preferCanvas
      style={{ height: '100%', width: '100%' }}
      scrollWheelZoom
      attributionControl={false}
    >
      <TileLayer
        url={isDark
          ? 'https://tile.jawg.io/jawg-dark/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
          : 'https://tile.jawg.io/jawg-light/{z}/{x}/{y}{r}.png?access-token=6FXDBzhanSef1HN4s1LZ3mPreirTHg2H5ErbrBakZuSCBpEww5wULxbgjz9EzrE6'
        }
      />
      <CoverageLayer
        premiumPoints={premium}
        stations={stations}
        radiusM={radiusM}
        color={color}
        showPremium={showPremium}
        showP1={showP1}
        dimmed={!!searchResult}
      />
      {showStationDots && (
        <StationDots
          premiumPoints={premium}
          stations={stations}
          showPremium={showPremium}
          showP1={showP1}
        />
      )}
      <InvalidateOnResize />
      <SearchResult result={searchResult} />
      <CardCornerPicker result={searchResult} cardEl={cardEl} onCorner={onCardCorner} />
    </MapContainer>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
function GPSLicenses() {
  const { user } = useAuth();
  const canEdit = canEditEquipment(user?.role);
  const navigate = useNavigate();
  const theme = useTheme();
  const formatExpiry = (iso) => _formatExpiryRaw(iso, user);

  const [activeTab, setActiveTab] = useState(0);

  // Snap non-admin users off the Reporting tab if they somehow land on it
  useEffect(() => {
    if (activeTab === 3 && user?.role !== 'admin') setActiveTab(0);
  }, [activeTab, user]);
  const [loading, setLoading] = useState(true);

  // Coverage Map tab (lazy-loaded on first open)
  const [coverage, setCoverage] = useState(null);
  const [coverageError, setCoverageError] = useState('');
  const [coverageLayer, setCoverageLayer] = useState('both');
  const [showStationDots, setShowStationDots] = useState(true);
  useEffect(() => {
    if (activeTab === 2 && !coverage && !coverageError) {
      fetchCoverage()
        .then(setCoverage)
        .catch(err => setCoverageError(err.message || 'Failed to load coverage'));
    }
  }, [activeTab, coverage, coverageError]);

  // Coverage Map → location search (drops a pin + nearest-station lines)
  const [coverageSearch, setCoverageSearch] = useState('');
  const [coverageSearching, setCoverageSearching] = useState(false);
  const [coverageSearchError, setCoverageSearchError] = useState('');
  const [searchResult, setSearchResult] = useState(null);
  // Result card anchoring: CardCornerPicker moves it off the default bottom-left
  // whenever a pin would end up underneath it.
  const [cardEl, setCardEl] = useState(null);
  const [cardCorner, setCardCorner] = useState('bottom-left');

  // Fit the map to the screen: its height runs from wherever it starts on the
  // page down to just above the footer, so the whole map (and a search result
  // framed inside it) is visible without scrolling. Re-measured when the window
  // or anything above the map (e.g. the credentials banner) changes size.
  const coverageMapRef = useRef(null);
  const [coverageMapHeight, setCoverageMapHeight] = useState(null);
  useEffect(() => {
    if (activeTab !== 2 || !coverage) return undefined;
    const measure = () => {
      const el = coverageMapRef.current;
      if (!el) return;
      const top = el.getBoundingClientRect().top + window.scrollY;
      setCoverageMapHeight(Math.max(COVERAGE_MIN_HEIGHT, Math.round(window.innerHeight - top - COVERAGE_BOTTOM_CLEARANCE)));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    ro.observe(document.body);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [activeTab, coverage]);

  const handleCoverageSearch = async () => {
    const q = coverageSearch.trim();
    if (!q || !coverage) return;
    setCoverageSearching(true);
    setCoverageSearchError('');
    try {
      let lat, lon, label;
      const coords = parseLatLon(q);
      if (coords) {
        [lat, lon] = coords;
        label = `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
      } else {
        const res = await locationCheck(q); // reused purely for geocoding
        if (res?.lat == null) throw new Error('Location not found');
        lat = res.lat;
        lon = res.lon;
        label = res.resolved_address || q;
      }
      const stations = coverage.pointone || [];
      const premium = coverage.premium || [];
      const p1 = stations.length ? nearestFrom(lat, lon, stations, s => s.lat, s => s.lon) : null;
      const pr = premium.length ? nearestFrom(lat, lon, premium, p => p[0], p => p[1]) : null;
      setSearchResult({
        lat, lon, label,
        pointone: p1 && p1.km <= COVERAGE_MAX_RANGE_KM
          ? { lat: p1.lat, lon: p1.lon, km: p1.km, name: p1.item.name } : null,
        premium: pr && pr.km <= COVERAGE_MAX_RANGE_KM
          ? { lat: pr.lat, lon: pr.lon, km: pr.km } : null,
      });
      // Bring the whole map on screen so the framed result isn't below the fold
      // (only matters on short windows where the map hit its minimum height).
      coverageMapRef.current?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    } catch (err) {
      setSearchResult(null);
      setCoverageSearchError(err.message || 'Search failed');
    } finally {
      setCoverageSearching(false);
    }
  };

  const clearCoverageSearch = () => {
    setSearchResult(null);
    setCoverageSearch('');
    setCoverageSearchError('');
  };

  // Machine tab data
  const [devices, setDevices] = useState([]);
  const [rtkDevices, setRtkDevices] = useState([]);
  const [gpsDevices, setGpsDevices] = useState([]);
  const [gpsDetails, setGpsDetails] = useState([]);
  const [rovers, setRovers] = useState([]);
  const [search, setSearch] = useState('');
  const [sortBy, setSortBy] = useState('last_active_at');
  const [sortDir, setSortDir] = useState('desc');

  // Reporting tab → Active Licenses table sort
  const [reportSortBy, setReportSortBy] = useState('days');
  const [reportSortDir, setReportSortDir] = useState('desc');

  // Reporting tab → Regional Usage pie: 'days' or 'active'
  const [regionPieMode, setRegionPieMode] = useState('days');

  // Reporting tab → Projected Year End Invoice grouping: 'region' or 'jobsite'
  const [invoiceMode, setInvoiceMode] = useState('region');

  // Reporting tab → Idleness table sort
  const [idleSortBy, setIdleSortBy] = useState('daysSinceActive');
  const [idleSortDir, setIdleSortDir] = useState('desc');

  // Reporting tab → Historic Licenses table sort
  const [histSortBy, setHistSortBy] = useState('ended_at');
  const [histSortDir, setHistSortDir] = useState('desc');

  // Rover Licenses tab table sort
  const [roverSortBy, setRoverSortBy] = useState('expires_at');
  const [roverSortDir, setRoverSortDir] = useState('asc');

  // Rover Licenses → license dialog. Admins get full manage on any active license;
  // BU managers get view-only NTRIP for any active license in their own BU.
  const [viewCheckout, setViewCheckout] = useState(null);
  const [viewVisible, setViewVisible] = useState({});
  const [viewBusy, setViewBusy] = useState(false);

  // Rover tab data
  const [checkouts, setCheckouts] = useState([]);
  const [rtkPoolAvailableCount, setRtkPoolAvailableCount] = useState(0);
  const [requestLocation, setRequestLocation] = useState('');
  const [roverSearch, setRoverSearch] = useState('');

  // Machine assignment dialog
  const [assignOpen, setAssignOpen] = useState(false);
  const [assignLicense, setAssignLicense] = useState(null);
  const [selectedAssignment, setSelectedAssignment] = useState('');
  const [deviceName, setDeviceName] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [assignError, setAssignError] = useState('');

  const [bulkAssigning, setBulkAssigning] = useState(false);

  const handleBulkAssign = async () => {
    setBulkAssigning(true);
    try {
      await bulkAssignLicenseNumbers();
      await loadAll();
    } catch (err) {
      console.error('Bulk assign failed:', err);
    } finally {
      setBulkAssigning(false);
    }
  };

  // Premium RTK detail dialog
  const [rtkDialogOpen, setRtkDialogOpen] = useState(false);
  const [rtkDialogDevice, setRtkDialogDevice] = useState(null);
  const [rtkDeviceName, setRtkDeviceName] = useState('');
  const [rtkSaving, setRtkSaving] = useState(false);
  const [rtkSaveError, setRtkSaveError] = useState('');
  const [rtkPassVisible, setRtkPassVisible] = useState(false);
  const [rtkDecommissioning, setRtkDecommissioning] = useState(false);
  const [rtkDecommissionError, setRtkDecommissionError] = useState('');
  const [rtkDecommissionOpen, setRtkDecommissionOpen] = useState(false);
  const [rtkDecommissionDevice, setRtkDecommissionDevice] = useState('');

  // Request dialog
  const [requestOpen, setRequestOpen] = useState(false);
  const [requestStep, setRequestStep] = useState(1);
  const [selectedDuration, setSelectedDuration] = useState('');
  const [customDate, setCustomDate] = useState('');
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState('');
  const [credentials, setCredentials] = useState(null);
  const [emailCopied, setEmailCopied] = useState(false);
  const copyEmail = () => {
    navigator.clipboard.writeText('mathieu.kippelen@menardusa.com');
    setEmailCopied(true);
    setTimeout(() => setEmailCopied(false), 5000);
  };
  const [advancedMode, setAdvancedMode] = useState(false);
  const [advancedProvider, setAdvancedProvider] = useState('');
  const [machineReqAdvancedMode, setMachineReqAdvancedMode] = useState(false);
  const [machineReqAdvancedProvider, setMachineReqAdvancedProvider] = useState('');
  const [provisionDeviceName, setProvisionDeviceName] = useState('');
  const [provisionPassword, setProvisionPassword] = useState('');
  const [provisionPlanId, setProvisionPlanId] = useState('mXKL');
  const units = useUnits();
  const [requestRegion, setRequestRegion] = useState('');
  const [requestBizUnit, setRequestBizUnit] = useState('');
  const [requestRegions, setRequestRegions] = useState([]);
  const [assignedToType, setAssignedToType] = useState('self');
  const [assignedToName, setAssignedToName] = useState('');
  const [selectedRoverId, setSelectedRoverId] = useState('');
  const [availableRegions, setAvailableRegions] = useState([]);

  // Machine request dialog
  const [machineReqOpen, setMachineReqOpen] = useState(false);
  const [machineReqStep, setMachineReqStep] = useState(1);
  const [machineReqGps, setMachineReqGps] = useState('');
  const [machineReqBizUnit, setMachineReqBizUnit] = useState('');
  const [machineReqRegion, setMachineReqRegion] = useState('');
  const [machineReqLocation, setMachineReqLocation] = useState('');
  const [machineReqBizUnits, setMachineReqBizUnits] = useState([]);
  const [machineReqRegions, setMachineReqRegions] = useState([]);
  const [machineReqLoading, setMachineReqLoading] = useState(false);
  const [machineReqError, setMachineReqError] = useState('');
  const [machineReqCredentials, setMachineReqCredentials] = useState(null);
  const [machineReqGpsMissing, setMachineReqGpsMissing] = useState([]);

  // Extend dialog
  const [extendOpen, setExtendOpen] = useState(false);
  const [extendCheckout, setExtendCheckout] = useState(null);
  const [extendDate, setExtendDate] = useState('');
  const [extending, setExtending] = useState(false);
  const [extendError, setExtendError] = useState('');

  // Reset dialog
  const [resetOpen, setResetOpen] = useState(false);
  const [resetDevice, setResetDevice] = useState('');

  // Billing aggregates (admin Reporting tab)
  const [billingByBu, setBillingByBu] = useState([]);
  const [licenseHistory, setLicenseHistory] = useState([]);

  const loadAll = async () => {
    try {
      const [licData, rtkData, gpsData, gpsSystemsData, roverData, checkoutData, countData, billingData, historyData] = await Promise.all([
        fetchLicenses().catch(() => ({ licenses: [] })),
        fetchRtkPremiumLicenses().catch(() => ({ licenses: [] })),
        fetchGPSData(),
        fetchGpsSystems(),
        fetchSurveyGps(),
        fetchLicenseCheckouts(),
        fetchAvailableCount().catch(() => ({ rtk_available_count: 0 })),
        fetchLicenseBilling().catch(() => ({ by_bu: [] })),
        fetchLicenseHistory().catch(() => ({ history: [] })),
      ]);
      setDevices(licData.licenses || []);
      setRtkDevices(rtkData.licenses || []);
      setGpsDevices(gpsData.results || []);
      setGpsDetails(gpsSystemsData.records || []);
      setRovers(roverData.rovers || []);
      setCheckouts(checkoutData.checkouts || []);
      setRtkPoolAvailableCount(countData.rtk_available_count || 0);
      setBillingByBu(billingData.by_bu || []);
      setLicenseHistory(historyData.history || []);
    } catch (err) {
      console.error('Error loading license data:', err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadAll();
    fetchRegions(undefined, user?.country).then(d => setAvailableRegions(d.regions || [])).catch(() => {});
  }, []);

  // ── Machine tab computed ──────────────────────────────────────────────────
  const licenseToGps = useMemo(() => {
    const map = {};
    for (const r of gpsDetails) {
      if (r.linked_license_id) map[r.linked_license_id] = r.gps_number;
    }
    return map;
  }, [gpsDetails]);

  const licenseToRover = useMemo(() => {
    const map = {};
    for (const r of rovers) {
      if (r.linked_license) map[r.linked_license] = r;
    }
    return map;
  }, [rovers]);

  const eligibleRovers = useMemo(
    () => (rovers || []).filter(r =>
      r.biz_unit === user?.businessUnit &&
      (r.status || 'Active') === 'Active' &&
      !r.linked_license
    ),
    [rovers, user]
  );

  const roverLabel = (r) => r.menard_internal_number || r.serial_number || `ID ${r.id}`;

  // Absolute Last Active moment in the viewer's preferred timezone (tooltip).
  // DB timestamps are UTC; P1N rows arrive without a timezone marker.
  const lastActiveTooltip = (ts) => {
    if (!ts) return '';
    const iso = /Z|[+-]\d{2}:\d{2}$/.test(ts) ? ts : ts + 'Z';
    return formatExpiryWithTime(iso, user);
  };

  const handleSort = (column) => {
    if (sortBy === column) setSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setSortBy(column); setSortDir(column === 'last_active_at' ? 'desc' : 'asc'); }
  };

  const handleReportSort = (column) => {
    if (reportSortBy === column) setReportSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setReportSortBy(column); setReportSortDir(column === 'days' ? 'desc' : 'asc'); }
  };

  const handleIdleSort = (column) => {
    if (idleSortBy === column) setIdleSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else {
      setIdleSortBy(column);
      setIdleSortDir(column === 'daysSinceActive' || column === 'lastActiveAt' ? 'desc' : 'asc');
    }
  };

  const handleHistSort = (column) => {
    if (histSortBy === column) setHistSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else {
      setHistSortBy(column);
      setHistSortDir(column === 'days' || column === 'ended_at' ? 'desc' : 'asc');
    }
  };

  const handleRoverSort = (column) => {
    if (roverSortBy === column) setRoverSortDir(prev => prev === 'asc' ? 'desc' : 'asc');
    else { setRoverSortBy(column); setRoverSortDir(column === 'last_active_at' ? 'desc' : 'asc'); }
  };

  // Combine Point One and Premium RTK devices for the machine tab
  const allMachineDevices = useMemo(() => {
    const pointone = devices.map(d => ({
      id: d.id,
      provider: 'pointone',
      label: d.label,
      menard_internal_number: d.menard_internal_number,
      machine_name: d.machine_name,
      region: d.region,
      biz_unit: d.biz_unit,
      country: d.country,
      last_active_at: d.last_active_at,
      connection_status: d.connection_status,
      created_at: d.created_at,
    }));
    const rtk = rtkDevices.map(d => ({
      id: d.id,
      provider: 'rtkpremium',
      label: d.device_name,
      menard_internal_number: d.menard_internal_number,
      machine_name: d.machine_name,
      region: d.region,
      biz_unit: d.biz_unit,
      country: d.country,
      last_active_at: d.last_active_at,
      // Live caster connectivity (GOOD / NO_POSITION / NO_CONNECTION), same
      // vocabulary as Point One. Subscription status is a separate concept and
      // stays on expiration_date / plan_name.
      connection_status: d.connection_status,
      solution_type: d.solution_type,
      expiration_date: d.expiration_date,
      plan_name: d.plan_name,
      ntrip_name: d.ntrip_name,
      ntrip_password: d.ntrip_password,
      serial_number: d.serial_number,
      created_at: d.created_at,
    }));
    return [...pointone, ...rtk];
  }, [devices, rtkDevices]);

  const filteredDevices = useMemo(() => {
    let result = allMachineDevices.filter(d => licenseToGps[d.id] || licenseToGps[String(d.id)]);
    if (['bu_manager', 'standard', 'technician'].includes(user?.role) && user?.businessUnit) {
      result = result.filter(d => d.biz_unit === user.businessUnit);
    }
    if (search.trim()) {
      const term = search.toLowerCase();
      result = result.filter(d => (d.label || '').toLowerCase().includes(term) || (d.menard_internal_number || '').toLowerCase().includes(term));
    }
    return [...result].sort((a, b) => {
      let valA, valB;
      if (sortBy === 'label' || sortBy === 'menard_internal_number') {
        valA = (a.menard_internal_number || a.label || '').toLowerCase();
        valB = (b.menard_internal_number || b.label || '').toLowerCase();
      } else if (sortBy === 'region') {
        valA = (a.region || '').toLowerCase();
        valB = (b.region || '').toLowerCase();
      } else if (sortBy === 'signed_out_to') {
        valA = (licenseToGps[a.id] || licenseToGps[String(a.id)] || '').toLowerCase();
        valB = (licenseToGps[b.id] || licenseToGps[String(b.id)] || '').toLowerCase();
      } else if (sortBy === 'connection_status') {
        const order = { 'GOOD': 0, 'NO_POSITION': 1, 'NO_CONNECTION': 3 };
        return sortDir === 'asc'
          ? (order[a.connection_status] ?? 2) - (order[b.connection_status] ?? 2)
          : (order[b.connection_status] ?? 2) - (order[a.connection_status] ?? 2);
      } else if (sortBy === 'last_active_at') {
        valA = a.last_active_at ? new Date(a.last_active_at).getTime() : 0;
        valB = b.last_active_at ? new Date(b.last_active_at).getTime() : 0;
      } else if (sortBy === 'machine_name') {
        valA = (a.machine_name || '').toLowerCase();
        valB = (b.machine_name || '').toLowerCase();
      } else if (sortBy === 'biz_unit') {
        valA = (a.biz_unit || '').toLowerCase();
        valB = (b.biz_unit || '').toLowerCase();
      } else if (sortBy === 'country') {
        valA = (a.country || '').toLowerCase();
        valB = (b.country || '').toLowerCase();
      }
      if (valA < valB) return sortDir === 'asc' ? -1 : 1;
      if (valA > valB) return sortDir === 'asc' ? 1 : -1;
      return 0;
    });
  }, [allMachineDevices, licenseToGps, search, sortBy, sortDir, user]);

  // ── Rover tab computed ────────────────────────────────────────────────────
  const myCheckouts = useMemo(() =>
    checkouts.filter(c => c.user_id === user?.id), [checkouts, user]);

  const sortedCheckouts = useMemo(() => {
    const term = roverSearch.trim().toLowerCase();
    const scopeToBu = ['bu_manager', 'standard', 'technician'].includes(user?.role) && user?.businessUnit;
    const dirMul = roverSortDir === 'asc' ? 1 : -1;
    const signedOutTo = (c) => c.assigned_to || c.full_name || c.email || '';
    return [...checkouts].filter(c => {
      const days = daysUntil(c.expires_at);
      if (days !== null && days <= 0) return false;
      if (scopeToBu && c.biz_unit !== user.businessUnit) return false;
      return !term || (c.full_name || '').toLowerCase().includes(term) || (c.assigned_to || '').toLowerCase().includes(term) || (c.region || '').toLowerCase().includes(term) || (c.biz_unit || '').toLowerCase().includes(term);
    }).sort((a, b) => {
      // Pin the logged-in user's own checkouts to the top regardless of column sort
      const aIsMine = a.user_id === user?.id ? 0 : 1;
      const bIsMine = b.user_id === user?.id ? 0 : 1;
      if (aIsMine !== bIsMine) return aIsMine - bIsMine;

      if (roverSortBy === 'expires_at') {
        // null expiry (no end date) sorts last regardless of direction
        if (!a.expires_at && !b.expires_at) return 0;
        if (!a.expires_at) return 1;
        if (!b.expires_at) return -1;
        return (new Date(a.expires_at) - new Date(b.expires_at)) * dirMul;
      }
      if (roverSortBy === 'signedOutTo') {
        const av = signedOutTo(a).toLowerCase();
        const bv = signedOutTo(b).toLowerCase();
        if (av < bv) return -1 * dirMul;
        if (av > bv) return 1 * dirMul;
        return 0;
      }
      if (roverSortBy === 'last_active_at') {
        // never-active sorts last regardless of direction
        if (!a.last_active_at && !b.last_active_at) return 0;
        if (!a.last_active_at) return 1;
        if (!b.last_active_at) return -1;
        return (new Date(a.last_active_at) - new Date(b.last_active_at)) * dirMul;
      }
      const av = (a[roverSortBy] || '').toString().toLowerCase();
      const bv = (b[roverSortBy] || '').toString().toLowerCase();
      if (av < bv) return -1 * dirMul;
      if (av > bv) return 1 * dirMul;
      return 0;
    });
  }, [checkouts, user, roverSearch, roverSortBy, roverSortDir]);

  const checkedOutIds = useMemo(() => new Set(
    checkouts.filter(c => c.provider !== 'rtkpremium').map(c => c.license_id)
  ), [checkouts]);
  const rtkCheckedOutIds = useMemo(() => new Set(
    checkouts.filter(c => c.provider === 'rtkpremium').map(c => c.license_id)
  ), [checkouts]);
  const poolAvailable = useMemo(() =>
    devices.filter(d => d.enabled && !checkedOutIds.has(d.id)).length, [devices, checkedOutIds]);
  const rtkPoolAvailable = useMemo(() =>
    rtkDevices.filter(d => d.subscription_status === 4 && !rtkCheckedOutIds.has(d.id)).length,
    [rtkDevices, rtkCheckedOutIds]);

  // ── Machine assignment handlers ───────────────────────────────────────────
  const handleCardClick = async (device) => {
    const currentGps = licenseToGps[device.id];
    const currentRover = licenseToRover[device.license_number];
    setAssignLicense(device);
    setSelectedAssignment(currentGps ? `gps:${currentGps}` : currentRover ? `rover:${currentRover.id}` : '');
    setAssignError('');
    setAssignOpen(true);
    if (device.menard_internal_number) {
      setDeviceName(device.menard_internal_number);
    } else {
      try {
        const nextNum = await fetchNextLicenseNumber(device.region || '');
        setDeviceName(nextNum);
      } catch {
        setDeviceName('');
      }
    }
  };

  const handleAssignClose = () => {
    setAssignOpen(false);
    setAssignLicense(null);
    setSelectedAssignment('');
    setDeviceName('');
    setAssignError('');
  };

  const handleAssign = async () => {
    if (!selectedAssignment) return;
    setAssigning(true);
    setAssignError('');
    try {
      const ops = [updateLicenseName(assignLicense.id, deviceName)];
      const prevGps = licenseToGps[assignLicense.id];
      const prevRover = licenseToRover[assignLicense.license_number];
      if (selectedAssignment.startsWith('gps:')) {
        const newGps = selectedAssignment.slice(4);
        if (prevRover) ops.push(assignLicenseToRover(prevRover.id, null));
        if (prevGps && prevGps !== newGps) await assignLicenseToGps(prevGps, null);
        ops.push(assignLicenseToGps(newGps, assignLicense.id));
      } else if (selectedAssignment.startsWith('rover:')) {
        const newRoverId = parseInt(selectedAssignment.slice(6));
        if (prevGps) ops.push(assignLicenseToGps(prevGps, null));
        if (prevRover && prevRover.id !== newRoverId) await assignLicenseToRover(prevRover.id, null);
        ops.push(assignLicenseToRover(newRoverId, assignLicense.license_number));
      }
      await Promise.all(ops);
      await loadAll();
      handleAssignClose();
    } catch (err) {
      setAssignError(err.message || 'Assignment failed');
    } finally {
      setAssigning(false);
    }
  };

  const handleUnlink = async () => {
    const prevGps = licenseToGps[assignLicense.id];
    const prevRover = licenseToRover[assignLicense.license_number];
    setAssigning(true);
    setAssignError('');
    try {
      const ops = [updateLicenseName(assignLicense.id, deviceName)];
      if (prevGps) ops.push(assignLicenseToGps(prevGps, null));
      if (prevRover) ops.push(assignLicenseToRover(prevRover.id, null));
      await Promise.all(ops);
      await loadAll();
      handleAssignClose();
    } catch (err) {
      setAssignError(err.message || 'Unlink failed');
    } finally {
      setAssigning(false);
    }
  };

  // ── Premium RTK dialog handlers ───────────────────────────────────────────
  const handleRtkCardClick = (device) => {
    setRtkDialogDevice(device);
    setRtkDeviceName(device.menard_internal_number || '');
    setRtkSaveError('');
    setRtkPassVisible(false);
    setRtkDialogOpen(true);
  };

  const handleRtkSave = async () => {
    setRtkSaving(true);
    setRtkSaveError('');
    try {
      await updateRtkPremiumLabel(rtkDialogDevice.id, rtkDeviceName);
      await loadAll();
      setRtkDialogOpen(false);
    } catch (err) {
      setRtkSaveError(err.message || 'Save failed');
    } finally {
      setRtkSaving(false);
    }
  };

  const handleRtkDecommission = async () => {
    if (!rtkDecommissionDevice) return;
    setRtkDecommissioning(true);
    setRtkDecommissionError('');
    try {
      const selectedDevice = filteredDevices.find(d => String(d.id) === String(rtkDecommissionDevice));
      if (selectedDevice?.provider === 'pointone') {
        await decommissionPointOneLicense(Number(rtkDecommissionDevice));
      } else {
        await decommissionRtkLicense(Number(rtkDecommissionDevice));
      }
      await loadAll();
      setRtkDecommissionOpen(false);
    } catch (err) {
      setRtkDecommissionError(err.message || 'Return failed');
    } finally {
      setRtkDecommissioning(false);
    }
  };

  // ── Request handlers ──────────────────────────────────────────────────────
  const openRequest = () => {
    setRequestStep(1);
    setSelectedDuration('');
    setCustomDate('');
    setProvisionDeviceName('');
    setProvisionPassword('');
    setProvisionPlanId('mXKL');
    setRequestRegion(user?.region || '');
    setRequestBizUnit(user?.businessUnit || '');
    setAssignedToType('self');
    setAssignedToName('');
    setSelectedRoverId('');
    setRequestError('');
    setCredentials(null);
    setRequestLocation('');
    setAdvancedMode(false);
    setAdvancedProvider('');
    if (user?.role === 'bu_manager' && user?.businessUnit) {
      fetchRegions(user.businessUnit, user.country).then(d => setRequestRegions(d.regions || [])).catch(() => {});
    }
    setRequestOpen(true);
  };

  const provisionForProvider = async (provider) => {
    const isBuManager = user?.role === 'bu_manager';
    const duration = selectedDuration === 'custom' ? `custom:${customDate}` : selectedDuration;
    const selectedRover = assignedToType === 'rover'
      ? eligibleRovers.find(r => String(r.id) === String(selectedRoverId))
      : null;
    const region = isBuManager
      ? (assignedToType === 'self' ? (user.region || '')
        : assignedToType === 'jobsite' ? (user.region || '')
        : assignedToType === 'rover' ? (selectedRover?.region || user.region || '')
        : requestRegion)
      : (user.region || '');
    const bizUnit = user.businessUnit || '';
    const assignedTo = assignedToType === 'self' ? null : assignedToName.trim() || null;
    const payload = {
      duration,
      userId: user.id,
      fullName: user.fullName || user.username,
      email: user.email || '',
      region,
      bizUnit,
      country: user.country || '',
      assignedTo,
      assignedToType: assignedToType === 'self' ? null : assignedToType,
      roverId: selectedRoverId ? Number(selectedRoverId) : null,
    };
    const result = provider === 'pointone'
      ? await provisionPointOne(payload)
      : await provisionRtkPremium(payload);
    setCredentials(result.credentials);
    setRequestStep(2);
  };

  const handleRequestSubmit = async () => {
    setRequestLoading(true);
    setRequestError('');
    try {
      // Advanced override: user has explicitly picked a provider — skip location check
      if (advancedMode && advancedProvider) {
        await provisionForProvider(advancedProvider);
        return;
      }

      // Regular flow: backend picks the vendor based on P1N → Premium → none
      const locResult = await locationCheck(requestLocation.trim());
      if (locResult?.vendor === 'pointone') {
        await provisionForProvider('pointone');
      } else if (locResult?.vendor === 'premium') {
        await provisionForProvider('rtkpremium');
      } else {
        setRequestStep(3);
      }
    } catch (err) {
      setRequestError(err.message || 'Request failed');
    } finally {
      setRequestLoading(false);
    }
  };

  const handleRequestClose = () => {
    setRequestOpen(false);
    if (requestStep === 2) loadAll(); // refresh after successful checkout
  };

  const openMachineRequest = () => {
    setMachineReqStep(1);
    setMachineReqGps('');
    setMachineReqBizUnit('');
    setMachineReqRegion('');
    setMachineReqLocation('');
    setMachineReqBizUnits([]);
    setMachineReqRegions([]);
    setMachineReqError('');
    setMachineReqCredentials(null);
    setMachineReqGpsMissing([]);
    setMachineReqAdvancedMode(false);
    setMachineReqAdvancedProvider('');
    fetchBusinessUnits(user?.country || 'United States').then(d => setMachineReqBizUnits(d.businessUnits || [])).catch(() => {});
    setMachineReqOpen(true);
  };

  const handleMachineReqGpsChange = async (gpsId) => {
    setMachineReqGps(gpsId);
    setMachineReqGpsMissing([]);
    const selectedGps = gpsDetails.find(g => String(g.id) === String(gpsId));
    const region = selectedGps?.region || '';
    setMachineReqRegion(region);
    let bu = selectedGps?.biz_unit || '';
    if (!bu && region) {
      try {
        const data = await fetchBusinessUnitForRegion(region, user?.country);
        bu = data.businessUnit || '';
      } catch {
        bu = '';
      }
    }
    setMachineReqBizUnit(bu);
    if (bu) fetchRegions(bu, user?.country).then(d => setMachineReqRegions(d.regions || [])).catch(() => {});
    if (selectedGps) {
      setMachineReqGpsMissing([...(!bu ? ['Business Unit'] : []), ...(!region ? ['Region'] : [])]);
    }
  };

  const handleMachineReqBizUnitChange = (bu) => {
    setMachineReqBizUnit(bu);
    setMachineReqRegion('');
    fetchRegions(bu, user?.country).then(d => setMachineReqRegions(d.regions || [])).catch(() => {});
  };

  const handleMachineReqSubmit = async () => {
    setMachineReqLoading(true);
    setMachineReqError('');
    try {
      let provider;
      if (machineReqAdvancedMode && machineReqAdvancedProvider) {
        // Advanced override: skip location check
        provider = machineReqAdvancedProvider;
      } else {
        const locResult = await locationCheck(machineReqLocation.trim());
        if (locResult?.vendor === 'pointone') {
          provider = 'pointone';
        } else if (locResult?.vendor === 'premium') {
          provider = 'rtkpremium';
        } else {
          setMachineReqStep(3);
          return;
        }
      }

      const selectedGps = gpsDetails.find(g => String(g.id) === String(machineReqGps));
      const machinePayload = {
        userId: user.id,
        fullName: user.fullName || user.username,
        email: user.email || '',
        region: machineReqRegion,
        bizUnit: machineReqBizUnit,
        country: user?.country || '',
        assignedTo: null,
        assignedToType: null,
        gpsSystemId: machineReqGps || null,
        gpsNumber: selectedGps?.gps_number || null,
        machineName: selectedGps?.machine_assignment || null,
      };
      const result = provider === 'pointone'
        ? await provisionPointOne({ ...machinePayload, duration: 'none' })
        : await provisionRtkPremium({ ...machinePayload, duration: '12m' });
      setMachineReqCredentials(result.credentials);
      setMachineReqStep(2);
    } catch (err) {
      setMachineReqError(err.message || 'Request failed');
    } finally {
      setMachineReqLoading(false);
    }
  };

  const handleMachineReqClose = () => {
    setMachineReqOpen(false);
    if (machineReqStep === 2) loadAll();
  };

  // ── Extend handlers ───────────────────────────────────────────────────────
  const openExtend = (checkout) => {
    setExtendCheckout(checkout);
    // Pre-fill with current expiry date in YYYY-MM-DD format
    const d = new Date(checkout.expires_at);
    setExtendDate(d.toISOString().split('T')[0]);
    setExtendError('');
    setExtendOpen(true);
  };

  const handleExtend = async () => {
    if (!extendDate) return;
    setExtending(true);
    setExtendError('');
    try {
      await extendLicenseCheckout(extendCheckout.id, extendDate);
      await loadAll();
      setExtendOpen(false);
    } catch (err) {
      setExtendError(err.message || 'Failed to extend');
    } finally {
      setExtending(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  const sortLabel = (col, label) => (
    <TableSortLabel
      active={sortBy === col}
      direction={sortBy === col ? sortDir : 'asc'}
      onClick={() => handleSort(col)}
      sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
    >
      <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>{label}</Typography>
    </TableSortLabel>
  );

  return (
    // Coverage tab: bottom padding = the footer clearance, so on a short window
    // (map at its minimum height) the page can still scroll the map's bottom edge
    // out from under the fixed footer. On taller windows it adds no scroll.
    <Box sx={{ pb: activeTab === 2 ? `${COVERAGE_BOTTOM_CLEARANCE}px` : 10 }}>
      {/* Credentials banner for logged-in user */}
      <CredentialsBanner checkouts={myCheckouts} onReturn={loadAll} onExtend={openExtend} formatExpiry={formatExpiry} />

      {/* Tabs */}
      <Tabs
        value={activeTab}
        onChange={(_, v) => setActiveTab(v)}
        // Four tabs don't fit a phone; without scrolling, Coverage Map and
        // Reporting sit off-screen with no way to reach them.
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 3, borderBottom: 1, borderColor: 'divider', '& .MuiTab-root': { minWidth: 'auto' } }}
      >
        <Tab label="Rover Licenses" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        <Tab label="Machine Licenses" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        <Tab label="Coverage Map" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        {user?.role === 'admin' && (
          <Tab label="Reporting" sx={{ fontWeight: 700, fontSize: { xs: '0.9rem', sm: '1rem' } }} />
        )}
      </Tabs>

      {/* ══ REPORTING TAB (admin only) ═══════════════════════════════════ */}
      {user?.role === 'admin' && activeTab === 3 && (() => {
        // Reporting floor — mirrors REPORTING_FLOOR_DATE in gps_web_server.py.
        // Days accrued before this date don't count toward billing/usage totals.
        const REPORTING_FLOOR_MS = new Date('2026-05-28T00:00:00Z').getTime();

        // Days-since helper. Applies the same billing rules as the backend:
        //  - Clamps to REPORTING_FLOOR_MS so pre-floor usage is ignored
        //  - Clamps end to expires_at so returned rovers stop accruing
        //  - Counts a calendar day (UTC) as 1 only if active overlap within that day >= 2 hours
        const USAGE_THRESHOLD_MS = 2 * 60 * 60 * 1000;
        const MS_PER_DAY = 86400000;
        const parseDays = (createdRaw, endRaw) => {
          if (!createdRaw) return null;
          const createdIso = createdRaw.endsWith('Z') ? createdRaw : createdRaw + 'Z';
          const created = new Date(createdIso);
          if (isNaN(created.getTime())) return null;
          const startMs = Math.max(created.getTime(), REPORTING_FLOOR_MS);
          let endMs = Date.now();
          if (endRaw) {
            const endIso = endRaw.endsWith('Z') ? endRaw : endRaw + 'Z';
            const ended = new Date(endIso);
            if (!isNaN(ended.getTime())) endMs = Math.min(endMs, ended.getTime());
          }
          if (endMs <= startMs) return 0;

          // UTC midnight of the start and end days
          const startDayMs = Math.floor(startMs / MS_PER_DAY) * MS_PER_DAY;
          const endDayMs = Math.floor(endMs / MS_PER_DAY) * MS_PER_DAY;

          if (startDayMs === endDayMs) {
            return (endMs - startMs) >= USAGE_THRESHOLD_MS ? 1 : 0;
          }

          let count = 0;
          // Start day: from startMs to its midnight
          if ((startDayMs + MS_PER_DAY) - startMs >= USAGE_THRESHOLD_MS) count += 1;
          // Middle days: each automatically qualifies (full 24h)
          const middleDays = Math.floor((endDayMs - startDayMs) / MS_PER_DAY) - 1;
          if (middleDays > 0) count += middleDays;
          // End day: from its midnight to endMs
          if (endMs - endDayMs >= USAGE_THRESHOLD_MS) count += 1;
          return count;
        };

        // Section 2 rows: currently-active licenses only (live snapshot)
        const roverRows = checkouts.map((c) => ({
          key: `r-${c.id}`,
          type: 'Rover',
          name: c.assigned_to || c.full_name || c.email || '—',
          assignedBy: c.assigned_to && c.full_name ? c.full_name : null,
          // Jobsite name when this checkout is assigned to a jobsite (AssignedToType === 'jobsite')
          jobsite: c.assigned_to_type === 'jobsite' ? (c.assigned_to || null) : null,
          biz_unit: c.biz_unit,
          country: c.country,
          region: c.region,
          provider: c.provider,
          // Clamp at expires_at so returned rovers stop accruing days post-return
          days: parseDays(c.created_at, c.expires_at),
        }));

        const machineRows = allMachineDevices
          .filter(d => licenseToGps[d.id] || licenseToGps[String(d.id)])
          .map((d) => ({
            key: `m-${d.provider}-${d.id}`,
            type: 'Machine',
            name: d.machine_name || d.label || '—',
            assignedBy: null,
            biz_unit: d.biz_unit,
            country: d.country,
            region: d.region,
            provider: d.provider,
            days: parseDays(d.created_at, null),
          }));

        // Restrict reporting to the logged-in user's own business unit + country.
        // Country scoping isolates each country's data so identically-named BUs across
        // countries don't collide once non-US business units come online.
        // Note: admins are NOT bypassed — they see their own BU like everyone else.
        // A separate global-view card (see todo) will surface cross-BU numbers for admins.
        const userBu = user?.businessUnit || '';
        const userCountry = user?.country || '';
        const inScope = (r) => userBu && r.biz_unit === userBu && (!userCountry || (r.country || 'United States') === userCountry);
        const allRows = [...roverRows, ...machineRows].filter(inScope);

        const sortDirMul = reportSortDir === 'asc' ? 1 : -1;
        const breakdown = [...allRows].sort((a, b) => {
          if (reportSortBy === 'days') {
            // Nulls sort last regardless of direction
            const av = a.days, bv = b.days;
            if (av == null && bv == null) return 0;
            if (av == null) return 1;
            if (bv == null) return -1;
            return (av - bv) * sortDirMul;
          }
          const av = (a[reportSortBy] || '').toString().toLowerCase();
          const bv = (b[reportSortBy] || '').toString().toLowerCase();
          if (!av && !bv) return 0;
          if (!av) return 1;
          if (!bv) return -1;
          if (av < bv) return -1 * sortDirMul;
          if (av > bv) return 1 * sortDirMul;
          return 0;
        });

        // Section 1 + billing card: use proper billing aggregates from /api/licenses/billing
        // (combines historical from LicenseUsageLog + active from live tables — accurate for billing).
        const buRows = billingByBu
          .filter(inScope)
          .map(r => ({
            bu: r.biz_unit,
            count: r.active_count,
            totalDays: r.total_days,
            historicalDays: r.historical_days,
            activeDays: r.active_days,
          }));

        const totalDays = Math.floor(buRows.reduce((sum, r) => sum + r.totalDays, 0));
        const fmtDays = (n) => {
          const d = Math.floor(n);
          return `${d.toLocaleString('en-US')} day${d !== 1 ? 's' : ''}`;
        };

        // ── Provider day split (active + historical combined) ─────────
        const historyForBu = licenseHistory.filter(inScope);
        const sumDays = (rows, predicate) => rows.filter(predicate).reduce((s, r) => s + (r.days || 0), 0);
        const p1nActiveDays = sumDays(breakdown, r => r.provider === 'pointone');
        const rtkActiveDays = sumDays(breakdown, r => r.provider === 'rtkpremium');
        const p1nHistDays = sumDays(historyForBu, r => r.provider === 'pointone');
        const rtkHistDays = sumDays(historyForBu, r => r.provider === 'rtkpremium');
        const p1nTotalDays = Math.floor(p1nActiveDays + p1nHistDays);
        const rtkTotalDays = Math.floor(rtkActiveDays + rtkHistDays);
        const totalActiveDays = Math.floor(buRows.reduce((s, r) => s + r.activeDays, 0));
        const totalHistoricalDays = Math.floor(buRows.reduce((s, r) => s + r.historicalDays, 0));
        const activePct = totalDays > 0 ? (totalActiveDays / totalDays) * 100 : 0;


        // Shared column widths so the three Reporting tables (Stale / Active / Historic)
        // line up column-for-column. Order: Signed Out To, Type, Region, Provider,
        // Ended, Reason, [numeric]. The final numeric column holds Days in all three.
        // A slot a table lacks is left blank. Used with table-layout: fixed.
        const RPT_W = {
          signedOutTo: '22%', type: '10%', region: '15%', provider: '15%',
          ended: '14%', reason: '14%', days: '10%',
        };
        // Stale and Active carry no Ended/Reason values, so they use five real
        // columns and spread the freed 28% across the rest.
        const RPT_W5 = {
          signedOutTo: '30%', type: '13%', region: '19%', provider: '24%', days: '14%',
        };

        const providerData = [
          ...(p1nTotalDays > 0 ? [{ name: 'PointOneNav', value: p1nTotalDays, fill: COLOR_P1N }] : []),
          ...(rtkTotalDays > 0 ? [{ name: 'Premium RTK', value: rtkTotalDays, fill: COLOR_RTK }] : []),
        ];

        const fmtDate = (iso) => {
          if (!iso) return '—';
          const d = new Date(iso);
          if (isNaN(d.getTime())) return '—';
          return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        };
        const reasonLabel = (r) => {
          if (!r) return '—';
          if (r === 'decommissioned') return 'Expired';
          return r.charAt(0).toUpperCase() + r.slice(1);
        };
        const reasonColor = (r) => {
          if (r === 'returned') return '#4a90e2';
          if (r === 'expired' || r === 'decommissioned') return '#ff8c00';
          return 'text.secondary';
        };

        // Active license counts by provider
        const p1nActiveCount = breakdown.filter(r => r.provider === 'pointone').length;
        const rtkActiveCount = breakdown.filter(r => r.provider === 'rtkpremium').length;
        // Deduped active-license count (rover checkouts + machine-assigned devices, each counted once).
        // Used for display instead of the billing active_count, which double-counts checked-out licenses.
        const activeLicenseCount = breakdown.length;

        // Active license counts by type (rover checkout vs machine license) for the BU summary tiles.
        const roverActiveCount = breakdown.filter(r => r.type === 'Rover').length;
        const machineActiveCount = breakdown.filter(r => r.type === 'Machine').length;

        // Active license counts by region (gradient card breakdown).
        // breakdown is already user-BU-scoped via inScope. Drop blank/null regions.
        const regionCounts = breakdown.reduce((acc, r) => {
          const region = (r.region || '').trim();
          if (!region) return acc;
          acc[region] = (acc[region] || 0) + 1;
          return acc;
        }, {});
        const regionEntries = Object.entries(regionCounts).sort((a, b) => b[1] - a[1]);

        // ── Banner stat cards: health · active · stale · inactive · projected year-end ──
        const cardsNowMs = Date.now();
        const parseUtcMs = (raw) => {
          if (!raw) return null;
          const iso = raw.endsWith('Z') ? raw : raw + 'Z';
          const ms = new Date(iso).getTime();
          return isNaN(ms) ? null : ms;
        };
        // In-scope device licenses (P1N + RTK) make up the BU's total license pool.
        const scopedDevices = allMachineDevices.filter(
          d => d.biz_unit === userBu && (!userCountry || (d.country || 'United States') === userCountry)
        );
        const totalPool = scopedDevices.length;
        // Staleness stays PointOneNav-only. RTK Premium now reports last_active_at
        // too, but its rovers reconnect in ~60s bursts rather than holding a
        // session, so the 15/30-day idle tiers aren't comparable across providers.
        const scopedP1n = scopedDevices.filter(d => d.provider === 'pointone');
        const daysIdle = (d) => {
          const lastMs = parseUtcMs(d.last_active_at);
          if (lastMs == null) return null; // never reported a position
          return Math.max(0, Math.floor((cardsNowMs - lastMs) / MS_PER_DAY));
        };
        // Non-overlapping tiers: stale = idle 15–29 days, inactive = idle 30+ days.
        // Never-active P1N licenses count as inactive (the most severe tier).
        const staleCount = scopedP1n.filter(d => { const n = daysIdle(d); return n != null && n >= 15 && n < 30; }).length;
        const inactiveCount = scopedP1n.filter(d => { const n = daysIdle(d); return n == null || n >= 30; }).length;
        const pctOfPool = (n) => totalPool > 0 ? Math.round((n / totalPool) * 100) : 0;

        // Roll the tiers up into one health status using the project's vivid status colours.
        let healthLabel = 'Healthy', healthColor = '#00ff41';
        if (inactiveCount > 0) { healthLabel = 'Critical'; healthColor = '#ff1a1a'; }
        else if (staleCount > 0) { healthLabel = 'Monitor'; healthColor = '#ffbf00'; }

        const bannerCards = [
          { label: 'Active Licenses', value: activeLicenseCount.toLocaleString('en-US'), accent: 'text.primary' },
          { label: 'Stale Licenses (15–29 days)', value: staleCount.toLocaleString('en-US'), sub: `${pctOfPool(staleCount)}% of total`, accent: '#ffbf00' },
          { label: 'Inactive (30+ days)', value: inactiveCount.toLocaleString('en-US'), sub: `${pctOfPool(inactiveCount)}% of total`, accent: '#ff1a1a' },
          { label: 'Estimated Year End Usage', value: 'Coming soon', placeholder: true, accent: 'text.secondary' },
        ];

        return (
          <Box>
            {/* ══ Header: title + business unit ══ */}
            <Box sx={{ mb: 2.5 }}>
              <Typography variant="h4" sx={{ color: 'text.primary', lineHeight: 1.2 }}>
                GPS Licenses Overview
              </Typography>
              <Typography variant="subtitle1" sx={{ color: 'text.secondary' }}>
                {userBu || '—'}
              </Typography>
            </Box>

            {/* ══ Banner: license health + key counts (BU + country scoped) ══ */}
            <Card sx={{ mb: 3 }}>
              <CardContent sx={{ py: 2.5 }}>
                <Box sx={{ display: 'flex', flexWrap: { xs: 'wrap', md: 'nowrap' }, alignItems: 'stretch' }}>
                  {/* License Pool Health */}
                  <Box sx={{ flex: 1, minWidth: { xs: '45%', md: 0 }, py: { xs: 1, md: 0 } }}>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                      License Pool Health
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1.5 }}>
                      <Box sx={{
                        width: 14, height: 14, borderRadius: '50%',
                        background: `radial-gradient(circle, ${healthColor} 0%, ${healthColor} 60%, transparent 100%)`,
                        boxShadow: `0 0 10px ${healthColor}`,
                        animation: 'healthPulse 1.6s ease-in-out infinite',
                        '@keyframes healthPulse': {
                          '0%, 100%': { boxShadow: `0 0 5px ${healthColor}`, opacity: 0.8 },
                          '50%': { boxShadow: `0 0 14px 3px ${healthColor}`, opacity: 1 },
                        },
                      }} />
                      <Typography variant="h5" fontWeight={800} sx={{ color: healthColor }}>
                        {healthLabel}
                      </Typography>
                    </Box>
                    {healthLabel === 'Critical' && (
                      <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                        The license pool is not being used efficiently.
                      </Typography>
                    )}
                  </Box>
                  {bannerCards.map((c) => (
                    <React.Fragment key={c.label}>
                      <Divider orientation="vertical" flexItem sx={{ mx: { xs: 1.5, md: 3 }, display: { xs: 'none', md: 'block' } }} />
                      <Box sx={{ flex: 1, minWidth: { xs: '45%', md: 0 }, py: { xs: 1, md: 0 } }}>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                          {c.label}
                        </Typography>
                        <Typography
                          variant={c.placeholder ? 'h6' : 'h4'}
                          fontWeight={c.placeholder ? 600 : 800}
                          sx={{ color: c.accent, mt: 1.5, fontStyle: c.placeholder ? 'italic' : 'normal' }}
                        >
                          {c.value}
                        </Typography>
                        {c.sub && (
                          <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mt: 0.5 }}>
                            {c.sub}
                          </Typography>
                        )}
                      </Box>
                    </React.Fragment>
                  ))}
                </Box>
              </CardContent>
            </Card>

            {/* ── Charts row: License Utilization + Usage by Region ──────── */}
            {/* Cards are 2-up until xl, 4-up above it. They were md={3} — 4 across from 900px —
                which left each donut column ~96px wide: the donut clipped, the legend truncated
                to "Gen...", and the centred % overflowed the hole. 4-up needs ~1536px to breathe. */}
            <Grid container spacing={2} sx={{ mb: 4 }}>
              {/* License Utilization donut — utilized (active) vs unused of the pool */}
              <Grid item xs={12} sm={6} xl={3}>
                {(() => {
                  // In Use = active PointOneNav licenses (utilization is a P1N-only signal:
                  // pool minus stale 15–29d and inactive 30+d/never). Total = the full BU
                  // license pool, including Premium RTK.
                  const inUse = Math.max(0, scopedP1n.length - (staleCount + inactiveCount));
                  const total = totalPool;
                  const remainder = Math.max(0, total - inUse);
                  const utilPct = total > 0 ? Math.round((inUse / total) * 100) : 0;
                  const utilData = [
                    { name: 'In Use', value: inUse, fill: '#00c853' },
                    { name: 'Not in use', value: remainder, fill: '#9e9e9e' },
                  ].filter(d => d.value > 0);
                  const fmtLic = (v) => `${v} license${v !== 1 ? 's' : ''}`;

                  return (
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                          License Utilization
                        </Typography>
                        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                          {userBu || 'Your BU'}
                        </Typography>
                        {total > 0 ? (
                          <Box sx={{ height: 240, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ position: 'relative', width: '55%', height: '100%' }}>
                              <ResponsiveContainer width="100%" height="100%">
                                <PieChart>
                                  {/* Radii are percentages, not px: Recharts resolves them
                                      against min(plotW,plotH)/2, so the donut scales with the
                                      card. Fixed 60/90 px overflowed the SVG and clipped once
                                      this column fell below ~180px (any window under ~1500px).
                                      This column is width-bound (~204px at desktop vs 240 tall),
                                      so 90%/60% reproduces the old 90/60 px look there. */}
                                  <Pie
                                    data={utilData}
                                    dataKey="value"
                                    cx="50%" cy="50%"
                                    innerRadius="60%" outerRadius="90%"
                                    paddingAngle={3}
                                    stroke="none"
                                  >
                                    {utilData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                  </Pie>
                                  <RechartsTooltip
                                    contentStyle={{
                                      backgroundColor: theme.palette.background.paper,
                                      border: `1px solid ${theme.palette.divider}`,
                                      borderRadius: 4,
                                      color: theme.palette.text.primary,
                                    }}
                                    itemStyle={{ color: theme.palette.text.primary }}
                                    labelStyle={{ color: theme.palette.text.primary }}
                                    formatter={(v) => fmtLic(v)}
                                  />
                                </PieChart>
                              </ResponsiveContainer>
                              <Box sx={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', pointerEvents: 'none' }}>
                                <Typography variant="h4" fontWeight={800} sx={{ color: '#00c853', lineHeight: 1 }}>
                                  {utilPct}%
                                </Typography>
                                <Typography variant="caption" color="text.secondary">In Use</Typography>
                              </Box>
                            </Box>
                            {/* minWidth: 0 — flex items default to min-width:auto, so without
                                it this legend refuses to shrink below its text and squeezes the
                                donut column under its 55%. */}
                            <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <ArrowUpIcon sx={{ color: '#00c853', fontSize: 28 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="h6" fontWeight={700} sx={{ color: '#00c853', lineHeight: 1 }}>{inUse}</Typography>
                                  <Typography variant="caption" color="text.secondary">In Use (PointOneNav)</Typography>
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Box sx={{ width: 14, height: 14, borderRadius: '50%', backgroundColor: '#9e9e9e', mx: '7px', flexShrink: 0 }} />
                                <Box sx={{ minWidth: 0 }}>
                                  <Typography variant="h6" fontWeight={700} sx={{ color: 'text.primary', lineHeight: 1 }}>{total}</Typography>
                                  <Typography variant="caption" color="text.secondary">Total</Typography>
                                </Box>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary">No licenses in your BU yet.</Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}
              </Grid>

              {/* Usage by Region donut — Days / License toggle */}
              <Grid item xs={12} sm={6} xl={3}>
                {(() => {
                  // Build per-region totals from BU-scoped breakdown + history.
                  const REGION_PALETTE = [
                    '#00c853', '#4a90e2', '#ff8c00', '#9c27b0',
                    '#00bcd4', '#ffbf00', '#e91e63', '#607d8b',
                  ];
                  const sumByRegion = (rows, valueFn) => rows.reduce((acc, r) => {
                    const region = (r.region || '').trim();
                    if (!region) return acc;
                    acc[region] = (acc[region] || 0) + valueFn(r);
                    return acc;
                  }, {});

                  const regionTotals = regionPieMode === 'active'
                    ? sumByRegion(breakdown, () => 1)
                    : (() => {
                        const fromActive = sumByRegion(breakdown, r => r.days || 0);
                        const fromHist = sumByRegion(historyForBu, r => r.days || 0);
                        const merged = { ...fromActive };
                        Object.entries(fromHist).forEach(([k, v]) => {
                          merged[k] = (merged[k] || 0) + v;
                        });
                        return merged;
                      })();

                  const regionPieData = Object.entries(regionTotals)
                    .filter(([, v]) => v > 0)
                    .sort((a, b) => b[1] - a[1])
                    .map(([name, value], i) => ({
                      name,
                      value: regionPieMode === 'days' ? Math.floor(value) : value,
                      fill: REGION_PALETTE[i % REGION_PALETTE.length],
                    }));

                  const pieTotal = regionPieData.reduce((s, d) => s + d.value, 0);
                  const fmtValue = (v) => regionPieMode === 'days' ? fmtDays(v) : `${v} license${v !== 1 ? 's' : ''}`;

                  return (
                    <Card sx={{ height: '100%' }}>
                      <CardContent>
                        <Box sx={{ mb: 1 }}>
                          <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                            Usage by Region
                          </Typography>
                          <Typography variant="h6" fontWeight={600}>
                            {userBu || 'Your BU'}
                          </Typography>
                        </Box>
                        <ToggleButtonGroup
                          value={regionPieMode}
                          exclusive
                          size="small"
                          onChange={(e, v) => { if (v) setRegionPieMode(v); }}
                          sx={{ mb: 1.5 }}
                        >
                          <ToggleButton value="days">Days</ToggleButton>
                          <ToggleButton value="active">License</ToggleButton>
                        </ToggleButtonGroup>
                        {regionPieData.length ? (
                          <Box sx={{ minHeight: 240, display: 'flex', alignItems: 'center' }}>
                            <Box sx={{ width: '55%', height: 240, flexShrink: 0 }}>
                              <ResponsiveContainer width="100%" height="100%">
                              <PieChart>
                                <Pie
                                  data={regionPieData}
                                  dataKey="value"
                                  cx="50%" cy="50%"
                                  innerRadius="51%" outerRadius="86%"
                                  paddingAngle={3}
                                  stroke="none"
                                >
                                  {regionPieData.map((d, i) => <Cell key={i} fill={d.fill} />)}
                                </Pie>
                                <RechartsTooltip
                                  contentStyle={{
                                    backgroundColor: theme.palette.background.paper,
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 4,
                                    color: theme.palette.text.primary,
                                  }}
                                  itemStyle={{ color: theme.palette.text.primary }}
                                  labelStyle={{ color: theme.palette.text.primary }}
                                  formatter={(v) => fmtValue(v)}
                                />
                              </PieChart>
                              </ResponsiveContainer>
                            </Box>
                            <Box sx={{ flex: 1, minWidth: 0, pl: 2 }}>
                              {regionPieData.map((d) => {
                                const pct = pieTotal > 0 ? Math.round((d.value / pieTotal) * 100) : 0;
                                return (
                                  <Box key={d.name} sx={{ display: 'flex', alignItems: 'flex-start', mb: 1.25 }}>
                                    <Box sx={{ width: 12, height: 12, borderRadius: '50%', backgroundColor: d.fill, mr: 1.5, mt: 0.4, flexShrink: 0 }} />
                                    <Box sx={{ minWidth: 0 }}>
                                      <Typography variant="body2" fontWeight={600} noWrap>{d.name}</Typography>
                                      <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                                        {fmtValue(d.value)} · {pct}%
                                      </Typography>
                                    </Box>
                                  </Box>
                                );
                              })}
                              <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                                <Typography variant="body2" fontWeight={700}>Total</Typography>
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block', fontWeight: 700 }}>
                                  {fmtValue(pieTotal)}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        ) : (
                          <Box sx={{ height: 240, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Typography variant="body2" color="text.secondary">No regional data yet.</Typography>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  );
                })()}
              </Grid>

              {/* Provider Breakdown — usage days split by provider, with logos */}
              <Grid item xs={12} sm={6} xl={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent>
                    <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                      Provider Breakdown
                    </Typography>
                    <Typography variant="h6" fontWeight={600} sx={{ mb: 2 }}>
                      {userBu || 'Your BU'}
                    </Typography>
                    {(() => {
                      const stat = (active, days) => (
                        <Box sx={{ display: 'flex', gap: 2.5, mt: 0.25 }}>
                          <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>{active}</Typography>
                            <Typography variant="caption" color="text.secondary">Active</Typography>
                          </Box>
                          <Box>
                            <Typography variant="h6" fontWeight={700} sx={{ lineHeight: 1.1 }}>{Math.floor(days).toLocaleString('en-US')}</Typography>
                            <Typography variant="caption" color="text.secondary">Est. Days</Typography>
                          </Box>
                        </Box>
                      );
                      return (
                        <>
                          {[
                            { provider: 'pointone', name: 'PointOneNav', active: p1nActiveCount, days: p1nTotalDays },
                            { provider: 'rtkpremium', name: 'Premium RTK', active: rtkActiveCount, days: rtkTotalDays },
                          ].map((p) => (
                            <Box key={p.provider} sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                              {p.provider === 'pointone' ? (
                                <Box sx={{
                                  width: 32, height: 32, flexShrink: 0,
                                  backgroundColor: '#418039',
                                  WebkitMaskImage: 'url(/p1n_logo.png)', WebkitMaskSize: 'contain', WebkitMaskPosition: 'center', WebkitMaskRepeat: 'no-repeat',
                                  maskImage: 'url(/p1n_logo.png)', maskSize: 'contain', maskPosition: 'center', maskRepeat: 'no-repeat',
                                }} />
                              ) : (
                                <Box component="img" src="/premium_rtk_logo.png" alt="Premium RTK" sx={{
                                  width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0, backgroundColor: '#fff',
                                }} />
                              )}
                              <Box sx={{ minWidth: 0 }}>
                                <Typography variant="body2" fontWeight={600} noWrap>{p.name}</Typography>
                                {stat(p.active, p.days)}
                              </Box>
                            </Box>
                          ))}
                          <Box sx={{ mt: 1, pt: 1.5, borderTop: 1, borderColor: 'divider' }}>
                            <Typography variant="body2" fontWeight={700}>Total</Typography>
                            {stat(activeLicenseCount, totalDays)}
                          </Box>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              </Grid>

              {/* Billing Cycle Summary — placeholder */}
              <Grid item xs={12} sm={6} xl={3}>
                <Card sx={{ height: '100%' }}>
                  <CardContent sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5, display: 'block' }}>
                        Billing Cycle Summary
                      </Typography>
                      <Typography variant="h6" fontWeight={600}>
                        {userBu || 'Your BU'}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        May 28, 2026 – May 28, 2027
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: 160 }}>
                      <Typography variant="h6" fontWeight={600} sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        Coming soon
                      </Typography>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* ── Idleness table (P1N only) ──────────────────────────────── */}
            {(() => {
              const NOW_MS = Date.now();
              // Backend returns timestamps without timezone — treat as UTC by appending Z
              // so the browser doesn't parse them as local time and produce negative days.
              const parseUtc = (raw) => {
                if (!raw) return null;
                const iso = raw.endsWith('Z') ? raw : raw + 'Z';
                const ms = new Date(iso).getTime();
                return isNaN(ms) ? null : ms;
              };
              // Map active P1N checkouts by license id so we can resolve "signed out to" person.
              const p1nCheckoutByLicense = checkouts.reduce((acc, c) => {
                if (c.provider !== 'rtkpremium' && c.license_id) acc[c.license_id] = c;
                return acc;
              }, {});
              const idleRows = allMachineDevices
                .filter(d => d.provider === 'pointone' && d.biz_unit === userBu &&
                  (!userCountry || (d.country || 'United States') === userCountry))
                .map(d => {
                  const lastMs = parseUtc(d.last_active_at);
                  const daysSince = lastMs != null
                    ? Math.max(0, Math.floor((NOW_MS - lastMs) / 86400000))
                    : null;

                  // Resolve who/what the license is signed out to.
                  // Priority: user checkout (rover license) > machine assignment > unassigned.
                  const checkout = p1nCheckoutByLicense[d.id];
                  let signedOutTo, assignmentDetail;
                  if (checkout) {
                    signedOutTo = checkout.full_name || checkout.email || '—';
                    // assigned_to is the BU-assigned target (rover/jobsite/person) — distinct from holder
                    assignmentDetail = checkout.assigned_to && checkout.assigned_to !== signedOutTo
                      ? checkout.assigned_to
                      : null;
                  } else if (d.machine_name) {
                    signedOutTo = d.machine_name;
                    assignmentDetail = null;
                  } else {
                    signedOutTo = 'Unassigned';
                    assignmentDetail = null;
                  }

                  return {
                    key: `idle-${d.id}`,
                    signedOutTo,
                    assignmentDetail,
                    type: checkout ? 'Rover' : 'Machine',
                    region: d.region || '—',
                    provider: d.provider,
                    lastActiveAt: d.last_active_at || null,
                    daysSinceActive: daysSince,
                  };
                })
                // Only surface stale licenses: 15+ days idle (or never active).
                .filter(r => r.daysSinceActive == null || r.daysSinceActive >= 15);

              const idleDirMul = idleSortDir === 'asc' ? 1 : -1;
              const sortedIdle = [...idleRows].sort((a, b) => {
                if (idleSortBy === 'daysSinceActive') {
                  // Nulls (never active) sort to top in desc, bottom in asc
                  const av = a.daysSinceActive, bv = b.daysSinceActive;
                  if (av == null && bv == null) return 0;
                  if (av == null) return idleSortDir === 'desc' ? -1 : 1;
                  if (bv == null) return idleSortDir === 'desc' ? 1 : -1;
                  return (av - bv) * idleDirMul;
                }
                const sortKey = idleSortBy === 'signedOutTo' ? 'signedOutTo' : idleSortBy;
                const av = (a[sortKey] || '').toString().toLowerCase();
                const bv = (b[sortKey] || '').toString().toLowerCase();
                if (av < bv) return -1 * idleDirMul;
                if (av > bv) return 1 * idleDirMul;
                return 0;
              });

              return (
                <Card sx={{ mb: 3 }}>
                  <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <SnoozeIcon sx={{ color: '#ff8c00' }} />
                    <Typography variant="h6" fontWeight={600}>Stale (PointOneNav Only)</Typography>
                    <Box sx={{ flex: 1 }} />
                    <Typography variant="caption" color="text.secondary">{sortedIdle.length} stale</Typography>
                  </Box>
                  {/* Phone: card rows (the table is unreadable at this width) */}
                  <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1, pb: 1 }}>
                    {sortedIdle.length ? sortedIdle.map((r) => (
                      <ReportRowCard
                        key={`c-${r.key}`}
                        name={r.signedOutTo}
                        detail={r.assignmentDetail}
                        type={r.type}
                        region={r.region}
                        provider={r.provider}
                        days={(
                          <Typography variant="body2" fontWeight={700} sx={{
                            color: r.daysSinceActive == null || r.daysSinceActive >= 30 ? '#ff1a1a' : '#ffbf00',
                          }}>
                            {r.daysSinceActive == null ? 'Never' : `${r.daysSinceActive} day${r.daysSinceActive === 1 ? '' : 's'}`}
                          </Typography>
                        )}
                      />
                    )) : (
                      <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                        No stale PointOneNav licenses (15+ days idle) for this BU.
                      </Typography>
                    )}
                  </Box>

                  <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                    <Table size="small" sx={{ tableLayout: 'fixed' }}>
                      <TableHead>
                        <TableRow sx={{ '& th': { fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', color: 'text.secondary', borderBottom: 1, borderColor: 'divider' } }}>
                          <TableCell sx={{ width: RPT_W5.signedOutTo }} sortDirection={idleSortBy === 'signedOutTo' ? idleSortDir : false}>
                            <TableSortLabel active={idleSortBy === 'signedOutTo'} direction={idleSortBy === 'signedOutTo' ? idleSortDir : 'asc'} onClick={() => handleIdleSort('signedOutTo')}>Signed Out To</TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ width: RPT_W5.type }} sortDirection={idleSortBy === 'type' ? idleSortDir : false}>
                            <TableSortLabel active={idleSortBy === 'type'} direction={idleSortBy === 'type' ? idleSortDir : 'asc'} onClick={() => handleIdleSort('type')}>Type</TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ width: RPT_W5.region }} sortDirection={idleSortBy === 'region' ? idleSortDir : false}>
                            <TableSortLabel active={idleSortBy === 'region'} direction={idleSortBy === 'region' ? idleSortDir : 'asc'} onClick={() => handleIdleSort('region')}>Region</TableSortLabel>
                          </TableCell>
                          <TableCell sx={{ width: RPT_W5.provider }}>Provider</TableCell>
                          <TableCell sx={{ width: RPT_W5.days }} align="right" sortDirection={idleSortBy === 'daysSinceActive' ? idleSortDir : false}>
                            <TableSortLabel active={idleSortBy === 'daysSinceActive'} direction={idleSortBy === 'daysSinceActive' ? idleSortDir : 'asc'} onClick={() => handleIdleSort('daysSinceActive')}>Days</TableSortLabel>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {sortedIdle.length ? sortedIdle.map((r) => (
                          <TableRow key={r.key} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                            <TableCell>
                              <Typography variant="body2" fontWeight={600} noWrap>{r.signedOutTo}</Typography>
                              {r.assignmentDetail && (
                                <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>{r.assignmentDetail}</Typography>
                              )}
                            </TableCell>
                            <TableCell>
                              <Box sx={{
                                display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600,
                                backgroundColor: r.type === 'Rover' ? 'rgba(74,144,226,0.15)' : 'rgba(0,200,83,0.15)',
                                color: r.type === 'Rover' ? COLOR_RTK : COLOR_P1N,
                              }}>{r.type}</Box>
                            </TableCell>
                            <TableCell><Typography variant="body2" noWrap>{r.region}</Typography></TableCell>
                            <TableCell>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: r.provider === 'pointone' ? COLOR_P1N : COLOR_RTK }} />
                                <Typography variant="body2" noWrap>{r.provider === 'pointone' ? 'PointOneNav' : 'Premium RTK'}</Typography>
                              </Box>
                            </TableCell>
                            <TableCell align="right">
                              <Typography variant="body2" fontWeight={600} sx={{
                                color: r.daysSinceActive == null || r.daysSinceActive >= 30 ? '#ff1a1a'
                                  : '#ffbf00',
                              }}>
                                {r.daysSinceActive == null ? 'Never' : `${r.daysSinceActive} day${r.daysSinceActive === 1 ? '' : 's'}`}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )) : (
                          <TableRow>
                            <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                              <Typography variant="body2" color="text.secondary">No stale PointOneNav licenses (15+ days idle) for this BU.</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Card>
              );
            })()}

            {/* ── Active licenses table ──────────────────────────────────── */}
            <Card sx={{ mb: 3 }}>
              <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <DevicesIcon sx={{ color: '#00c853' }} />
                <Typography variant="h6" fontWeight={600}>Active Licenses</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">{breakdown.length} active</Typography>
              </Box>
              {/* Phone: card rows (the table is unreadable at this width) */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1, pb: 1 }}>
                {breakdown.length ? breakdown.map((r) => (
                  <ReportRowCard
                    key={`c-${r.key}`}
                    name={r.name}
                    detail={r.assignedBy ? `by ${r.assignedBy}` : null}
                    type={r.type}
                    region={r.region}
                    provider={r.provider}
                    days={(
                      <Typography variant="body2" fontWeight={700}>
                        {r.days != null ? `${r.days} day${r.days === 1 ? '' : 's'}` : '—'}
                      </Typography>
                    )}
                  />
                )) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No active licenses.
                  </Typography>
                )}
              </Box>

              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', color: 'text.secondary', borderBottom: 1, borderColor: 'divider' } }}>
                      <TableCell sx={{ width: RPT_W5.signedOutTo }} sortDirection={reportSortBy === 'name' ? reportSortDir : false}>
                        <TableSortLabel active={reportSortBy === 'name'} direction={reportSortBy === 'name' ? reportSortDir : 'asc'} onClick={() => handleReportSort('name')}>Signed Out To</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W5.type }} sortDirection={reportSortBy === 'type' ? reportSortDir : false}>
                        <TableSortLabel active={reportSortBy === 'type'} direction={reportSortBy === 'type' ? reportSortDir : 'asc'} onClick={() => handleReportSort('type')}>Type</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W5.region }} sortDirection={reportSortBy === 'region' ? reportSortDir : false}>
                        <TableSortLabel active={reportSortBy === 'region'} direction={reportSortBy === 'region' ? reportSortDir : 'asc'} onClick={() => handleReportSort('region')}>Region</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W5.provider }} sortDirection={reportSortBy === 'provider' ? reportSortDir : false}>
                        <TableSortLabel active={reportSortBy === 'provider'} direction={reportSortBy === 'provider' ? reportSortDir : 'asc'} onClick={() => handleReportSort('provider')}>Provider</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W5.days }} align="right" sortDirection={reportSortBy === 'days' ? reportSortDir : false}>
                        <TableSortLabel active={reportSortBy === 'days'} direction={reportSortBy === 'days' ? reportSortDir : 'asc'} onClick={() => handleReportSort('days')}>Days</TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {breakdown.length ? breakdown.map((r) => (
                      <TableRow key={r.key} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <TableCell>
                          <Typography variant="body2" fontWeight={600} noWrap>{r.name}</Typography>
                          {r.assignedBy && (
                            <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>by {r.assignedBy}</Typography>
                          )}
                        </TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: r.type === 'Rover' ? 'rgba(74,144,226,0.15)' : 'rgba(0,200,83,0.15)',
                            color: r.type === 'Rover' ? COLOR_RTK : COLOR_P1N,
                          }}>{r.type}</Box>
                        </TableCell>
                        <TableCell><Typography variant="body2" noWrap>{r.region || '—'}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: r.provider === 'pointone' ? COLOR_P1N : COLOR_RTK }} />
                            <Typography variant="body2" noWrap>{r.provider === 'pointone' ? 'PointOneNav' : 'Premium RTK'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right"><Typography variant="body2" fontWeight={600}>{r.days != null ? r.days : '—'}</Typography></TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={5} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">No active licenses.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>

            {/* ── Projected Year End Invoice (by region) ─────────────────── */}
            {(() => {
              // Per-region usage days = active (breakdown) + historical days, same basis as the
              // Usage by Region donut in Days mode.
              const regionDays = [...breakdown, ...historyForBu].reduce((acc, r) => {
                const region = (r.region || '').trim() || '—';
                acc[region] = (acc[region] || 0) + (r.days || 0);
                return acc;
              }, {});
              const regionRows = Object.entries(regionDays)
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([region, days]) => ({ region, days: Math.floor(days) }));
              const totalRegionDays = regionRows.reduce((s, r) => s + r.days, 0);

              // By Jobsite: only checkouts assigned to a jobsite (AssignedToType === 'jobsite')
              // carry a jobsite name, so group those active rover rows by jobsite.
              const jobsiteDays = breakdown.reduce((acc, r) => {
                if (!r.jobsite) return acc;
                acc[r.jobsite] = (acc[r.jobsite] || 0) + (r.days || 0);
                return acc;
              }, {});
              const jobsiteRows = Object.entries(jobsiteDays)
                .filter(([, v]) => v > 0)
                .sort((a, b) => b[1] - a[1])
                .map(([jobsite, days]) => ({ jobsite, days: Math.floor(days) }));
              const totalJobsiteDays = jobsiteRows.reduce((s, r) => s + r.days, 0);
              return (
                <Card sx={{ mb: 3 }}>
                  <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <RequestQuoteIcon sx={{ color: '#00c853' }} />
                    <Typography variant="h6" fontWeight={600}>Projected Year End Invoice</Typography>
                    <Box sx={{ flex: 1 }} />
                    <ToggleButtonGroup
                      value={invoiceMode}
                      exclusive
                      size="small"
                      onChange={(e, v) => { if (v) setInvoiceMode(v); }}
                      sx={{ mr: 1 }}
                    >
                      <ToggleButton value="region">By Region</ToggleButton>
                      <ToggleButton value="jobsite">By Jobsite</ToggleButton>
                    </ToggleButtonGroup>
                    <Typography variant="caption" color="text.secondary">May 28, 2026 – May 28, 2027</Typography>
                  </Box>
                  {(() => {
                    const isJobsite = invoiceMode === 'jobsite';
                    const rows = isJobsite ? jobsiteRows : regionRows;
                    const totalDaysCol = isJobsite ? totalJobsiteDays : totalRegionDays;
                    const groupLabel = isJobsite ? 'Jobsite' : 'Region';
                    const emptyMsg = isJobsite ? 'No jobsite-assigned usage yet.' : 'No regional usage yet.';
                    return (
                      <TableContainer>
                        <Table size="small" sx={{ minWidth: { xs: 460, md: 'auto' } }}>
                          <TableHead>
                            <TableRow sx={{ '& th': { fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', color: 'text.secondary', borderBottom: 1, borderColor: 'divider' } }}>
                              <TableCell>{groupLabel}</TableCell>
                              <TableCell align="right">Est. Days</TableCell>
                              <TableCell align="right">Est. Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {rows.length ? rows.map((r) => (
                              <TableRow key={r.region || r.jobsite} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                                <TableCell><Typography variant="body2" noWrap>{r.region || r.jobsite}</Typography></TableCell>
                                <TableCell align="right"><Typography variant="body2">{r.days.toLocaleString('en-US')}</Typography></TableCell>
                                <TableCell align="right" />
                              </TableRow>
                            )) : (
                              <TableRow>
                                <TableCell colSpan={3} align="center" sx={{ py: 4 }}>
                                  <Typography variant="body2" color="text.secondary">{emptyMsg}</Typography>
                                </TableCell>
                              </TableRow>
                            )}
                            <TableRow sx={{ '& td': { borderTop: 2, borderColor: 'divider' } }}>
                              <TableCell><Typography variant="body2" fontWeight={700}>Total</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={700}>{totalDaysCol.toLocaleString('en-US')}</Typography></TableCell>
                              <TableCell align="right"><Typography variant="body2" fontWeight={600} sx={{ fontStyle: 'italic', color: 'text.secondary' }}>Coming soon</Typography></TableCell>
                            </TableRow>
                          </TableBody>
                        </Table>
                      </TableContainer>
                    );
                  })()}
                </Card>
              );
            })()}

            {/* ── Historic licenses table ────────────────────────────────── */}
            {(() => {
              const histDirMul = histSortDir === 'asc' ? 1 : -1;
              const sortedHistory = [...historyForBu].sort((a, b) => {
                if (histSortBy === 'days') {
                  const av = a.days ?? 0;
                  const bv = b.days ?? 0;
                  return (av - bv) * histDirMul;
                }
                if (histSortBy === 'ended_at') {
                  const av = a.ended_at ? new Date(a.ended_at).getTime() : 0;
                  const bv = b.ended_at ? new Date(b.ended_at).getTime() : 0;
                  return (av - bv) * histDirMul;
                }
                const av = (a[histSortBy] || '').toString().toLowerCase();
                const bv = (b[histSortBy] || '').toString().toLowerCase();
                if (av < bv) return -1 * histDirMul;
                if (av > bv) return 1 * histDirMul;
                return 0;
              });
              return (
            <Card>
              <Box sx={{ p: 2, pb: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <ArchiveIcon sx={{ color: '#9e9e9e' }} />
                <Typography variant="h6" fontWeight={600}>Historic Licenses</Typography>
                <Box sx={{ flex: 1 }} />
                <Typography variant="caption" color="text.secondary">{sortedHistory.length} ended</Typography>
              </Box>
              {/* Phone: card rows, same as the Stale/Active tables above */}
              <Box sx={{ display: { xs: 'block', md: 'none' }, px: 1, pb: 1 }}>
                {sortedHistory.length ? sortedHistory.map((h, idx) => (
                  <ReportRowCard
                    key={`hist-c-${idx}`}
                    name={h.name}
                    type={h.type}
                    region={h.region}
                    provider={h.provider}
                    days={(
                      <Typography variant="body2" fontWeight={700}>
                        {h.days != null ? `${h.days} day${h.days === 1 ? '' : 's'}` : '—'}
                      </Typography>
                    )}
                    extra={(
                      <>
                        <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                          Ended {fmtDate(h.ended_at)}
                        </Typography>
                        <Typography variant="caption" sx={{ color: reasonColor(h.reason), fontWeight: 700 }}>
                          {reasonLabel(h.reason)}
                        </Typography>
                      </>
                    )}
                  />
                )) : (
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 3 }}>
                    No historic licenses.
                  </Typography>
                )}
              </Box>

              <TableContainer sx={{ display: { xs: 'none', md: 'block' } }}>
                <Table size="small" sx={{ tableLayout: 'fixed' }}>
                  <TableHead>
                    <TableRow sx={{ '& th': { fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.7rem', color: 'text.secondary', borderBottom: 1, borderColor: 'divider' } }}>
                      <TableCell sx={{ width: RPT_W.signedOutTo }} sortDirection={histSortBy === 'name' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'name'} direction={histSortBy === 'name' ? histSortDir : 'asc'} onClick={() => handleHistSort('name')}>Signed Out To</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W.type }} sortDirection={histSortBy === 'type' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'type'} direction={histSortBy === 'type' ? histSortDir : 'asc'} onClick={() => handleHistSort('type')}>Type</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W.region }} sortDirection={histSortBy === 'region' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'region'} direction={histSortBy === 'region' ? histSortDir : 'asc'} onClick={() => handleHistSort('region')}>Region</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W.provider }} sortDirection={histSortBy === 'provider' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'provider'} direction={histSortBy === 'provider' ? histSortDir : 'asc'} onClick={() => handleHistSort('provider')}>Provider</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W.ended }} sortDirection={histSortBy === 'ended_at' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'ended_at'} direction={histSortBy === 'ended_at' ? histSortDir : 'asc'} onClick={() => handleHistSort('ended_at')}>Ended</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W.reason }} sortDirection={histSortBy === 'reason' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'reason'} direction={histSortBy === 'reason' ? histSortDir : 'asc'} onClick={() => handleHistSort('reason')}>Reason</TableSortLabel>
                      </TableCell>
                      <TableCell sx={{ width: RPT_W.days }} align="right" sortDirection={histSortBy === 'days' ? histSortDir : false}>
                        <TableSortLabel active={histSortBy === 'days'} direction={histSortBy === 'days' ? histSortDir : 'asc'} onClick={() => handleHistSort('days')}>Days</TableSortLabel>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {sortedHistory.length ? sortedHistory.map((h, idx) => (
                      <TableRow key={`hist-${idx}`} sx={{ '&:hover': { backgroundColor: 'action.hover' } }}>
                        <TableCell><Typography variant="body2" fontWeight={600} noWrap>{h.name}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{
                            display: 'inline-block', px: 1, py: 0.25, borderRadius: 1, fontSize: '0.7rem', fontWeight: 600,
                            backgroundColor: h.type === 'Rover' ? 'rgba(74,144,226,0.15)' : 'rgba(0,200,83,0.15)',
                            color: h.type === 'Rover' ? COLOR_RTK : COLOR_P1N,
                          }}>{h.type}</Box>
                        </TableCell>
                        <TableCell><Typography variant="body2" noWrap>{h.region || '—'}</Typography></TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Box sx={{ width: 8, height: 8, borderRadius: '50%', flexShrink: 0, backgroundColor: h.provider === 'pointone' ? COLOR_P1N : COLOR_RTK }} />
                            <Typography variant="body2" noWrap>{h.provider === 'pointone' ? 'PointOneNav' : 'Premium RTK'}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell><Typography variant="body2" noWrap>{fmtDate(h.ended_at)}</Typography></TableCell>
                        <TableCell>
                          <Typography variant="body2" noWrap sx={{ color: reasonColor(h.reason), fontWeight: 600 }}>
                            {reasonLabel(h.reason)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right"><Typography variant="body2" fontWeight={600}>{h.days != null ? h.days : '—'}</Typography></TableCell>
                      </TableRow>
                    )) : (
                      <TableRow>
                        <TableCell colSpan={7} align="center" sx={{ py: 4 }}>
                          <Typography variant="body2" color="text.secondary">No historic licenses.</Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
              );
            })()}
          </Box>
        );
      })()}

      {/* ══ COVERAGE MAP TAB ══════════════════════════════════════════════ */}
      {activeTab === 2 && (
        <Box>
          {/* Compact on phones: the heading block was eating ~150px of map */}
          <Box sx={{ mb: { xs: 1, md: 2 } }}>
            <Typography variant="h6" fontWeight={700} sx={{ fontSize: { xs: '1rem', md: '1.25rem' } }}>RTK Network Coverage</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.72rem', md: '0.875rem' }, lineHeight: { xs: 1.35, md: 1.43 } }}>
              Areas where a correction service provides RTK — each point covers ~50 km. Use the toggle to see Point One, Premium, or both.
            </Typography>
          </Box>
          {/* Wraps on a phone — the provider toggle ran off the right edge */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2, flexWrap: { xs: 'wrap', md: 'nowrap' } }}>
            {/* Mostly-white (mostly-dark in dark mode) backgrounds so the page
                texture doesn't bleed through; input/button text uses
                text.primary to match the selector's selected state (CM1/CM3). */}
            <TextField
              size="small"
              placeholder="Search address or lat, lon"
              value={coverageSearch}
              onChange={(e) => setCoverageSearch(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') handleCoverageSearch(); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment> }}
              sx={{
                flex: '0 1 340px',
                minWidth: 160,
                '& .MuiOutlinedInput-root': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(35,35,35,0.9)' : 'rgba(255,255,255,0.9)',
                },
                '& .MuiOutlinedInput-input': { color: 'text.primary' },
              }}
            />
            <Button
              variant="contained"
              onClick={handleCoverageSearch}
              disabled={coverageSearching || !coverage || !coverageSearch.trim()}
              sx={{
                flexShrink: 0,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(35,35,35,0.9)' : 'rgba(255,255,255,0.9)',
                color: 'text.primary',
                '&:hover': {
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(35,35,35,1)' : '#fff',
                },
              }}
            >
              {coverageSearching ? 'Searching…' : 'Search'}
            </Button>
            {(searchResult || coverageSearch) && (
              <Button variant="text" onClick={clearCoverageSearch} sx={{ flexShrink: 0 }}>Clear</Button>
            )}
            {coverageSearchError && (
              <Typography variant="body2" color="error" noWrap sx={{ minWidth: 0 }}>{coverageSearchError}</Typography>
            )}
            <ToggleButtonGroup
              value={coverageLayer}
              exclusive
              size="small"
              onChange={(_, v) => v && setCoverageLayer(v)}
              sx={{
                ml: { xs: 0, md: 'auto' },
                flexShrink: 0,
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(35,35,35,0.9)' : 'rgba(255,255,255,0.9)',
              }}
            >
              <ToggleButton value="both">Both</ToggleButton>
              <ToggleButton value="pointone">Point One</ToggleButton>
              <ToggleButton value="premium">Premium</ToggleButton>
            </ToggleButtonGroup>
          </Box>
          {coverageError ? (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="error">{coverageError}</Typography>
            </Paper>
          ) : !coverage ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>
          ) : (
            <Paper ref={coverageMapRef} sx={{ position: 'relative', height: coverageMapHeight ?? '70vh', minHeight: COVERAGE_MIN_HEIGHT, borderRadius: 2, overflow: 'hidden' }}>
              <CoverageMap coverage={coverage} layer={coverageLayer} isDark={theme.palette.mode === 'dark'} searchResult={searchResult} country={user?.country} showStationDots={showStationDots} cardEl={cardEl} onCardCorner={setCardCorner} />
              <Box sx={{ position: 'absolute', top: 12, right: 12, zIndex: 1000, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, px: 1, py: 0.25, boxShadow: 2 }}>
                <FormControlLabel
                  control={<Switch size="small" checked={showStationDots} onChange={(e) => setShowStationDots(e.target.checked)} />}
                  label={<Typography variant="caption" fontWeight={600}>Station Pins</Typography>}
                  sx={{ m: 0 }}
                />
              </Box>
              {/* Search result card — bottom-left by default, re-anchored by
                  CardCornerPicker whenever a pin would sit underneath it (CM7) */}
              {searchResult && (
                <Box ref={setCardEl} sx={{ position: 'absolute', ...CARD_CORNERS[cardCorner], zIndex: 1000, bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 1, px: 1.5, py: 1, boxShadow: 2, maxWidth: 320 }}>
                  <Typography variant="body2" fontWeight={700} sx={{ mb: 0.5 }}>{searchResult.label}</Typography>
                  {searchResult.pointone && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        component="svg"
                        viewBox="0 0 24 24"
                        sx={{ width: 16, height: 16, flexShrink: 0 }}
                        dangerouslySetInnerHTML={{ __html: `<g fill="${COVERAGE_COLORS.pointone}">${STATION_PATHS}</g>` }}
                      />
                      <Box>
                        <Typography variant="body2">Point One: <strong>{fmtDistance(searchResult.pointone.km, units)}</strong></Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.25 }}>
                          Est. accuracy: {fmtAccuracy(searchResult.pointone.km, units)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {searchResult.premium && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        component="svg"
                        viewBox="0 0 24 24"
                        sx={{ width: 16, height: 16, flexShrink: 0 }}
                        dangerouslySetInnerHTML={{ __html: `<g fill="${COVERAGE_COLORS.premium}">${STATION_PATHS}</g>` }}
                      />
                      <Box>
                        <Typography variant="body2">Premium: <strong>{fmtDistance(searchResult.premium.km, units)}</strong></Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: -0.25 }}>
                          Est. accuracy: {fmtAccuracy(searchResult.premium.km, units)}
                        </Typography>
                      </Box>
                    </Box>
                  )}
                  {!searchResult.pointone && !searchResult.premium && (
                    <Typography variant="body2" color="text.secondary">No coverage within {fmtDistance(COVERAGE_MAX_RANGE_KM, units)}</Typography>
                  )}
                </Box>
              )}
            </Paper>
          )}
        </Box>
      )}

      {/* ══ MACHINE LICENSES TAB ══════════════════════════════════════════ */}
      {activeTab === 1 && (
        <>
          {/* Stats + controls */}
          <Grid container spacing={2} alignItems="center" sx={{ mb: 3 }}>
            {/* Point One Nav — commented out
            <Grid item xs={6} sm={3}>
              <StatsCard
                label="Active Last 24hrs"
                value={devices.filter(d => d.last_active_at && (Date.now() - new Date(d.last_active_at).getTime()) < 86400000).length}
              />
            </Grid>
            */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                <TextField
                  placeholder="Search devices..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  sx={{
                    width: 220,
                    '& .MuiOutlinedInput-root': {
                      height: 44,
                      backgroundColor: 'background.paper',
                      '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {['admin', 'technician'].includes(user?.role) && (
                  <>
                    <Button
                      variant="contained"
                      onClick={openMachineRequest}
                      sx={{
                        background: '#ffb700',
                        border: 'none',
                        '&:hover': { background: '#e0a500' },
                        boxShadow: 'none',
                        '&:active': { boxShadow: 'none' },
                        height: 44, color: '#000',
                        fontSize: '0.95rem', fontWeight: 600,
                        px: 3, textTransform: 'none', whiteSpace: 'nowrap',
                      }}
                    >
                      Request License +
                    </Button>
                    <Tooltip title="Return License" arrow>
                      <IconButton
                        onClick={() => { setRtkDecommissionDevice(''); setRtkDecommissionError(''); setRtkDecommissioning(false); setRtkDecommissionOpen(true); }}
                        sx={{ backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' }, width: 44, height: 44, borderRadius: 1 }}
                      >
                        <RemoveIcon />
                      </IconButton>
                    </Tooltip>
                    {/* Point One Nav reset button — commented out
                    <IconButton
                      onClick={() => setResetOpen(true)}
                      sx={{ backgroundColor: 'error.main', color: 'white', '&:hover': { backgroundColor: 'error.dark' }, width: 36, height: 36, borderRadius: 1 }}
                    >
                      <RemoveIcon />
                    </IconButton>
                    */}
                  </>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Column header */}
          <Paper sx={{ display: { xs: 'none', md: 'block' }, mb: 0.75, px: 2, py: 1.25, backgroundColor: 'secondary.main', color: 'white', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item sm={0.75}>{sortLabel('connection_status', 'Status')}</Grid>
              <Grid item sm={2}>{sortLabel('signed_out_to', 'Signed Out To')}</Grid>
              <Grid item sm={1.75}>{sortLabel('machine_name', 'Machine')}</Grid>
              <Grid item sm={1.5}>{sortLabel('region', 'Region')}</Grid>
              <Grid item sm={1.75}>{sortLabel('biz_unit', 'Business Unit')}</Grid>
              <Grid item sm={1.75}>{sortLabel('country', 'Country')}</Grid>
              <Grid item sm={1.75}>{sortLabel('last_active_at', 'Last Active')}</Grid>
              <Grid item sm={0.75} />
            </Grid>
          </Paper>

          {filteredDevices.map((device) => {
            const status = device.connection_status || 'UNKNOWN';
            const isRtk = device.provider === 'rtkpremium';
            const assignedGps = licenseToGps[device.id] || licenseToGps[String(device.id)] || null;
            const canViewNtrip = ['admin', 'technician'].includes(user?.role);
            return (
              <Card
                key={`${device.provider}-${device.id}`}
                onClick={() => assignedGps && navigate(`/equipment/machines/gps-health?gps=${encodeURIComponent(assignedGps)}`)}
                sx={{ mb: 0.75, border: 1, borderColor: 'divider', cursor: assignedGps ? 'pointer' : 'default', transition: 'all 0.2s', '&:hover': assignedGps ? { transform: 'translateY(-2px)', boxShadow: 4 } : {} }}
              >
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={2} sm={0.75}>
                      {isRtk ? (
                        <Tooltip title={status === 'ACTIVE' ? 'Active Subscription' : 'No Active Subscription'} arrow>
                          <Box sx={{
                            width: 32, height: 32, borderRadius: '50%',
                            background: status === 'ACTIVE'
                              ? 'radial-gradient(circle, #00ff41 45%, #00ff4188 55%, #00ff4122 70%, transparent 80%)'
                              : 'radial-gradient(circle, #ff1a1a 45%, #ff1a1a88 55%, #ff1a1a22 70%, transparent 80%)',
                            flexShrink: 0,
                          }} />
                        </Tooltip>
                      ) : (
                        <StatusDot status={status} />
                      )}
                    </Grid>
                    <Grid item xs={6} sm={2}>
                      <Typography variant="body1" fontWeight={800}>{assignedGps || '—'}</Typography>
                    </Grid>
                    <Grid item xs={6} sm={1.75}><Typography variant="body1">{device.machine_name || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.5}><Typography variant="body1">{device.region || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.75}><Typography variant="body1">{device.biz_unit || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.75}><Typography variant="body1">{device.country || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.75}>
                      <Tooltip title={lastActiveTooltip(device.last_active_at)} arrow>
                        <Typography variant="body1" sx={{ color: device.last_active_at ? 'text.primary' : 'text.secondary', width: 'fit-content' }}>
                          {getRelativeTime(device.last_active_at)}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sm={0.75} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title={device.provider === 'pointone' ? 'PointOneNav' : 'Premium RTK'} arrow>
                        {device.provider === 'pointone' ? (
                          <Box sx={{
                            width: 32, height: 32, flexShrink: 0,
                            backgroundColor: '#418039',
                            WebkitMaskImage: 'url(/p1n_logo.png)',
                            WebkitMaskSize: 'contain',
                            WebkitMaskPosition: 'center',
                            WebkitMaskRepeat: 'no-repeat',
                            maskImage: 'url(/p1n_logo.png)',
                            maskSize: 'contain',
                            maskPosition: 'center',
                            maskRepeat: 'no-repeat',
                          }} />
                        ) : (
                          <Box
                            component="img"
                            src="/premium_rtk_logo.png"
                            alt="Premium RTK"
                            sx={{
                              width: 32, height: 32, borderRadius: '50%',
                              objectFit: 'cover', flexShrink: 0,
                              backgroundColor: '#fff',
                            }}
                          />
                        )}
                      </Tooltip>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}

          {filteredDevices.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No devices found.</Typography>
            </Paper>
          )}
        </>
      )}

      {/* ══ ROVER LICENSES TAB ════════════════════════════════════════════ */}
      {activeTab === 0 && (
        <>
          {/* Search + Request button */}
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', justifyContent: { xs: 'flex-start', sm: 'flex-end' }, height: '100%' }}>
                <TextField
                  placeholder="Search..."
                  value={roverSearch}
                  onChange={(e) => setRoverSearch(e.target.value)}
                  sx={{
                    width: 220,
                    '& .MuiOutlinedInput-root': {
                      height: 44,
                      backgroundColor: 'background.paper',
                      '& fieldset': { borderWidth: 2, borderColor: 'primary.main' },
                    },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" sx={{ color: 'primary.main' }} />
                      </InputAdornment>
                    ),
                  }}
                />
                {['admin', 'technician', 'bu_manager', 'standard'].includes(user?.role) && (
                  <Button
                    variant="contained"
                    onClick={openRequest}
                    sx={{
                      background: '#ffb700',
                      border: 'none',
                      '&:hover': { background: '#e0a500' },
                      boxShadow: 'none',
                      '&:active': { boxShadow: 'none' },
                      height: 44, color: '#000',
                      fontSize: '0.95rem', fontWeight: 600,
                      px: 3,
                      textTransform: 'none', whiteSpace: 'nowrap',
                    }}
                  >
                    Request License +
                  </Button>
                )}
              </Box>
            </Grid>
          </Grid>

          {/* Column header */}
          <Paper sx={{ display: { xs: 'none', md: 'block' }, mb: 0.75, px: 2, py: 1.5, backgroundColor: 'secondary.main', color: 'white', borderRadius: 1 }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item sm={0.75}>
                <TableSortLabel
                  active={roverSortBy === 'connection_status'}
                  direction={roverSortBy === 'connection_status' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('connection_status')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Status</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={2}>
                <TableSortLabel
                  active={roverSortBy === 'signedOutTo'}
                  direction={roverSortBy === 'signedOutTo' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('signedOutTo')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Signed Out To</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={1.5}>
                <TableSortLabel
                  active={roverSortBy === 'region'}
                  direction={roverSortBy === 'region' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('region')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Region</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={1.75}>
                <TableSortLabel
                  active={roverSortBy === 'biz_unit'}
                  direction={roverSortBy === 'biz_unit' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('biz_unit')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Business Unit</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={1.75}>
                <TableSortLabel
                  active={roverSortBy === 'country'}
                  direction={roverSortBy === 'country' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('country')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Country</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={1.75}>
                <TableSortLabel
                  active={roverSortBy === 'last_active_at'}
                  direction={roverSortBy === 'last_active_at' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('last_active_at')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Last Active</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={1.5}>
                <TableSortLabel
                  active={roverSortBy === 'expires_at'}
                  direction={roverSortBy === 'expires_at' ? roverSortDir : 'asc'}
                  onClick={() => handleRoverSort('expires_at')}
                  sx={{ color: 'white', '&.Mui-active': { color: 'white' }, '& .MuiTableSortLabel-icon': { color: 'white !important' } }}
                >
                  <Typography variant="body2" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5, lineHeight: '1.5rem', whiteSpace: 'nowrap' }}>Expires</Typography>
                </TableSortLabel>
              </Grid>
              <Grid item sm={1} />
            </Grid>
          </Paper>

          {sortedCheckouts.map((c) => {
            const days = daysUntil(c.expires_at);
            const hours = hoursUntil(c.expires_at);
            const noEndDate = !c.expires_at;
            const underDay = days !== null && days > 0 && hours !== null && hours <= 24;
            const isMine = c.user_id === user?.id;
            const expiryColor = !isMine || noEndDate ? 'text.primary' : days <= 0 ? '#ff1a1a' : days <= 7 ? '#ff8c00' : 'text.primary';
            const rowBorderColor = isMine && days !== null && days <= 7 ? '#ff8c00' : 'divider';
            // Admins can open any active license (any BU) with full Return/Extend.
            // A BUA can open any active license in their own BU to view NTRIP creds
            // (view-only — no Return/Extend on licenses that aren't theirs).
            const isActiveRow = days === null || days > 0;
            const canOpen = isActiveRow && (
              user?.role === 'admin'
              || (user?.role === 'bu_manager' && !isMine && c.biz_unit === user?.businessUnit)
            );
            return (
              <Card
                key={c.id}
                onClick={canOpen ? () => { setViewCheckout(c); setViewVisible({}); } : undefined}
                sx={{
                  mb: 0.75,
                  border: rowBorderColor === '#ff8c00' ? 3 : 1,
                  borderColor: rowBorderColor,
                  transition: 'all 0.2s',
                  cursor: canOpen ? 'pointer' : 'default',
                  '&:hover': canOpen ? { transform: 'translateY(-1px)', boxShadow: 3, borderColor: 'primary.main' } : {},
                }}
              >
                <CardContent sx={{ py: 1, '&:last-child': { pb: 1 } }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={2} sm={0.75}><StatusDot status={c.connection_status || 'UNKNOWN'} /></Grid>
                    <Grid item xs={10} sm={2}>
                      <Typography variant="body1" fontWeight={800}>
                        {c.assigned_to || c.full_name || c.email || '—'}
                      </Typography>
                      {c.assigned_to && c.full_name && (
                        <Typography variant="caption" color="text.secondary">
                          Assigned by: {c.full_name}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6} sm={1.5}><Typography variant="body1">{c.region || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.75}><Typography variant="body1">{c.biz_unit || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.75}><Typography variant="body1">{c.country || '—'}</Typography></Grid>
                    <Grid item xs={6} sm={1.75}>
                      <Tooltip title={lastActiveTooltip(c.last_active_at)} arrow>
                        <Typography variant="body1" sx={{ color: c.last_active_at ? 'text.primary' : 'text.secondary', width: 'fit-content' }}>
                          {getRelativeTime(c.last_active_at)}
                        </Typography>
                      </Tooltip>
                    </Grid>
                    <Grid item xs={6} sm={1.5}>
                      {c.auto_renew ? (
                        <Typography variant="body1" fontWeight={600} sx={{ color: isMine ? '#00c853' : 'text.primary' }}>
                          Auto-renew
                        </Typography>
                      ) : (
                        <Typography variant="body1" fontWeight={600} sx={{ color: expiryColor }}>
                          {noEndDate ? 'No End Date' : formatExpiry(c.expires_at)}
                        </Typography>
                      )}
                    </Grid>
                    <Grid item xs={6} sm={1} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Tooltip title={c.provider === 'pointone' ? 'PointOneNav' : 'Premium RTK'} arrow>
                        {c.provider === 'pointone' ? (
                          <Box sx={{
                            width: 32, height: 32, flexShrink: 0,
                            backgroundColor: '#418039',
                            WebkitMaskImage: 'url(/p1n_logo.png)',
                            WebkitMaskSize: 'contain',
                            WebkitMaskPosition: 'center',
                            WebkitMaskRepeat: 'no-repeat',
                            maskImage: 'url(/p1n_logo.png)',
                            maskSize: 'contain',
                            maskPosition: 'center',
                            maskRepeat: 'no-repeat',
                          }} />
                        ) : (
                          <Box
                            component="img"
                            src="/premium_rtk_logo.png"
                            alt="Premium RTK"
                            sx={{
                              width: 32, height: 32, borderRadius: '50%',
                              objectFit: 'cover', flexShrink: 0,
                              backgroundColor: '#fff',
                            }}
                          />
                        )}
                      </Tooltip>
                    </Grid>
                  </Grid>
                </CardContent>
              </Card>
            );
          })}

          {sortedCheckouts.length === 0 && (
            <Paper sx={{ p: 4, textAlign: 'center' }}>
              <Typography color="text.secondary">No licenses currently checked out.</Typography>
            </Paper>
          )}

          {/* License dialog: admins manage any active license; BU managers view NTRIP only */}
          <Dialog open={Boolean(viewCheckout)} onClose={() => !viewBusy && setViewCheckout(null)} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ pb: 0.5 }}>
              <Typography variant="h6" fontWeight={700}>
                {viewCheckout?.assigned_to || viewCheckout?.full_name || viewCheckout?.email || '—'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {viewCheckout?.assigned_to && viewCheckout?.full_name ? `Assigned by ${viewCheckout.full_name} · ` : ''}
                {viewCheckout?.biz_unit || '—'}{viewCheckout?.region ? ` · ${viewCheckout.region}` : ''}
              </Typography>
            </DialogTitle>
            <DialogContent sx={{ pt: 2 }}>
              {viewCheckout && (() => {
                const fields = [
                  { label: 'Address', value: viewCheckout.ntrip_caster_url || (viewCheckout.provider === 'rtkpremium' ? 'rtkpremium.xyz' : 'truertk.pointonenav.com') },
                  { label: 'Port', value: '2101' },
                  { label: 'Mountpoint', value: viewCheckout.ntrip_mount_point || (viewCheckout.provider === 'rtkpremium' ? 'COMMON' : 'POLARIS_LOCAL') },
                  { label: 'Username', value: viewCheckout.ntrip_login || '—' },
                  { label: 'Password', value: viewCheckout.ntrip_password || '—', secret: true },
                ];
                return (
                  <Grid container spacing={1.5}>
                    {fields.map(({ label, value, secret }) => (
                      <Grid item xs={12} sm={6} key={label}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                            <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                              {secret && !viewVisible[label] ? '••••••••' : value}
                            </Typography>
                          </Box>
                          <Box sx={{ display: 'flex', flexShrink: 0 }}>
                            {secret && (
                              <IconButton size="small" onClick={() => setViewVisible(v => ({ ...v, [label]: !v[label] }))} sx={{ opacity: 0.6 }}>
                                {viewVisible[label] ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                              </IconButton>
                            )}
                            <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Box>
                        </Box>
                      </Grid>
                    ))}
                  </Grid>
                );
              })()}
              {viewCheckout && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="caption" color="text.secondary">
                    {viewCheckout.auto_renew
                      ? 'Auto-renew enabled'
                      : viewCheckout.expires_at
                        ? `Expires ${formatExpiry(viewCheckout.expires_at)}`
                        : 'No end date'}
                  </Typography>
                </Box>
              )}
            </DialogContent>
            <DialogActions sx={{ px: 3, pb: 2, justifyContent: user?.role === 'admin' ? 'space-between' : 'flex-end', gap: 1 }}>
              {user?.role === 'admin' && (
                <>
                  <Button
                    color="error"
                    variant="outlined"
                    disabled={viewBusy}
                    onClick={async () => {
                      if (!viewCheckout) return;
                      setViewBusy(true);
                      try { await returnLicenseCheckout(viewCheckout.id); await loadAll(); }
                      catch {}
                      finally { setViewBusy(false); setViewCheckout(null); }
                    }}
                    sx={{ minWidth: 90 }}
                  >
                    Return
                  </Button>
                  {!viewCheckout?.auto_renew && (
                    <Button
                      variant="outlined"
                      disabled={viewBusy}
                      onClick={() => { const c = viewCheckout; setViewCheckout(null); openExtend(c); }}
                      sx={{ minWidth: 90 }}
                    >
                      Extend
                    </Button>
                  )}
                  <Box sx={{ flex: 1 }} />
                </>
              )}
              <Button onClick={() => setViewCheckout(null)} disabled={viewBusy} sx={{ minWidth: 90 }}>Close</Button>
            </DialogActions>
          </Dialog>
        </>
      )}

      {/* ══ MACHINE ASSIGNMENT DIALOG (Point One Nav — commented out) ════
      <Dialog open={assignOpen} onClose={handleAssignClose} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>{assignLicense?.label}</Typography>
          <IconButton onClick={handleAssignClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {canEdit ? (
              <>
                <TextField
                  label="Device Name"
                  size="small"
                  fullWidth
                  value={deviceName}
                  onChange={(e) => setDeviceName(e.target.value)}
                  helperText={`Point One label: ${assignLicense?.label || ''}`}
                />
                <FormControl size="small" fullWidth>
                  <InputLabel>Assign License</InputLabel>
                  <Select value={selectedAssignment} label="Assign License" onChange={(e) => setSelectedAssignment(e.target.value)}>
                    <MenuItem value=""><em>None</em></MenuItem>
                    <ListSubheader>GPS Systems</ListSubheader>
                    {gpsDevices.map((g) => (
                      <MenuItem key={`gps:${g.gps_num}`} value={`gps:${g.gps_num}`}>
                        {g.gps_num}{g.machine ? ` — ${g.machine}` : ''}
                      </MenuItem>
                    ))}
                    <ListSubheader>GPS Rovers</ListSubheader>
                    {rovers.map((r) => (
                      <MenuItem key={`rover:${r.id}`} value={`rover:${r.id}`}>
                        {r.menard_internal_number || r.serial_number || `ID ${r.id}`}
                        {r.model ? ` — ${r.model}` : ''}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                {assignError && <Typography color="error" variant="body2">{assignError}</Typography>}
              </>
            ) : (
              <>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Device Name</Typography>
                  <Typography variant="body1" fontWeight={500}>{deviceName || '—'}</Typography>
                  <Typography variant="caption" color="text.secondary">Point One label: {assignLicense?.label || ''}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Assigned To</Typography>
                  <Typography variant="body1" fontWeight={500}>
                    {selectedAssignment ? selectedAssignment.replace('gps:', 'GPS ').replace('rover:', 'Rover ') : '—'}
                  </Typography>
                </Box>
              </>
            )}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          {canEdit && (licenseToGps[assignLicense?.id] || licenseToRover[assignLicense?.license_number]) && (
            <Button color="error" onClick={handleUnlink} disabled={assigning}>Unlink All</Button>
          )}
          <Box sx={{ flex: 1 }} />
          <Button onClick={handleAssignClose}>Close</Button>
          {canEdit && (
            <Button variant="contained" onClick={handleAssign} disabled={assigning || !selectedAssignment}>
              {assigning ? 'Saving...' : 'Assign'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
      ══ END MACHINE ASSIGNMENT DIALOG ══════════════════════════════════ */}

      {/* ══ RTK PREMIUM DETAIL DIALOG ════════════════════════════════════ */}
      <Dialog open={rtkDialogOpen} onClose={() => setRtkDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" fontWeight={700}>{rtkDialogDevice?.label}</Typography>
          <IconButton onClick={() => setRtkDialogOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Status</Typography>
              <Typography variant="body1" fontWeight={600} sx={{ color: getHealthColor(rtkDialogDevice?.connection_status) }}>
                {getHealthLabel(rtkDialogDevice?.connection_status)}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {getRelativeTime(rtkDialogDevice?.last_active_at)}
                {rtkDialogDevice?.solution_type ? ` · ${rtkDialogDevice.solution_type.replace(/_/g, ' ')}` : ''}
              </Typography>
            </Box>

            <Divider />

            {/* NTRIP credentials */}
            <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>NTRIP Credentials</Typography>
            {[
              { label: 'Username', value: rtkDialogDevice?.ntrip_name, secret: false },
              { label: 'Password', value: rtkDialogDevice?.ntrip_password, secret: true },
              { label: 'Address', value: 'rtkpremium.xyz' },
              { label: 'Port', value: '2101' },
              { label: 'Mountpoint', value: 'COMMON' },
            ].map(({ label, value, secret }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
                <Box sx={{ minWidth: 0 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                  <Typography variant="body2" fontWeight={600} sx={{ wordBreak: 'break-all' }}>
                    {secret && !rtkPassVisible ? '••••••••' : (value || '—')}
                  </Typography>
                </Box>
                <Box sx={{ display: 'flex', flexShrink: 0 }}>
                  {secret && (
                    <IconButton size="small" onClick={() => setRtkPassVisible(v => !v)} sx={{ opacity: 0.6 }}>
                      {rtkPassVisible ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                    </IconButton>
                  )}
                  {value && !value.startsWith('TBD') && (
                    <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                      <CopyIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            ))}

          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setRtkDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* ══ REQUEST DIALOG — Step 1: Duration ════════════════════════════ */}
      <Dialog open={requestOpen && requestStep === 1} onClose={() => setRequestOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Request License
          <IconButton onClick={() => setRequestOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {advancedMode ? (
              <FormControl fullWidth size="small">
                <InputLabel>NTRIP Provider</InputLabel>
                <Select
                  value={advancedProvider}
                  label="NTRIP Provider"
                  onChange={(e) => setAdvancedProvider(e.target.value)}
                >
                  <MenuItem value="pointone">PointOneNav</MenuItem>
                  <MenuItem value="rtkpremium">Premium RTK</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Location"
                size="small"
                fullWidth
                value={requestLocation}
                onChange={(e) => setRequestLocation(e.target.value)}
                placeholder="e.g. Toronto, ON  or  43.5927, -79.6463"
              />
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Duration</InputLabel>
              <Select value={selectedDuration} label="Duration" onChange={(e) => { setSelectedDuration(e.target.value); setCustomDate(''); }}>
                <MenuItem value="7d">7 Days</MenuItem>
                <MenuItem value="30d">30 Days</MenuItem>
                <MenuItem value="6m">6 Months</MenuItem>
                <MenuItem value="12m">12 Months</MenuItem>
                <MenuItem value="custom">Custom Date</MenuItem>
                <MenuItem value="auto-renew">No Expiration</MenuItem>
              </Select>
            </FormControl>
            {selectedDuration === 'custom' && (
              <TextField
                label="End Date"
                type="date"
                size="small"
                fullWidth
                value={customDate}
                onChange={(e) => setCustomDate(e.target.value)}
                InputLabelProps={{ shrink: true }}
                inputProps={{ min: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })() }}
              />
            )}
            {user?.role === 'bu_manager' && (
              <>
                <FormControl fullWidth size="small">
                  <InputLabel>Assign To</InputLabel>
                  <Select value={assignedToType} label="Assign To" onChange={(e) => { setAssignedToType(e.target.value); setAssignedToName(''); setSelectedRoverId(''); setRequestRegion(''); setRequestBizUnit(''); }}>
                    <MenuItem value="self">Myself</MenuItem>
                    <MenuItem value="person">Person</MenuItem>
                    <MenuItem value="jobsite">Jobsite</MenuItem>
                    <MenuItem value="rover">Rover</MenuItem>
                  </Select>
                </FormControl>
                {(assignedToType === 'person' || assignedToType === 'jobsite') && (
                  <>
                    <TextField
                      label={assignedToType === 'person' ? 'Person Name' : 'Jobsite Name'}
                      size="small"
                      fullWidth
                      value={assignedToName}
                      onChange={(e) => setAssignedToName(e.target.value)}
                      placeholder={assignedToType === 'person' ? 'e.g. John Smith' : 'e.g. Chicago Site A'}
                    />
                    {assignedToType === 'person' && (
                      <FormControl fullWidth size="small">
                        <InputLabel>Region</InputLabel>
                        <Select value={requestRegion} label="Region" onChange={(e) => setRequestRegion(e.target.value)}>
                          <MenuItem value=""><em>Select region</em></MenuItem>
                          {[...requestRegions, ...(!requestRegions.includes('General') ? ['General'] : [])].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
                        </Select>
                      </FormControl>
                    )}
                  </>
                )}
                {assignedToType === 'rover' && (
                  <FormControl fullWidth size="small">
                    <InputLabel>Rover</InputLabel>
                    <Select
                      value={selectedRoverId}
                      label="Rover"
                      onChange={(e) => {
                        const id = e.target.value;
                        setSelectedRoverId(id);
                        const r = eligibleRovers.find(x => String(x.id) === String(id));
                        setAssignedToName(r ? roverLabel(r) : '');
                      }}
                    >
                      {eligibleRovers.length === 0 ? (
                        <MenuItem value="" disabled><em>No available rovers in your Business Unit</em></MenuItem>
                      ) : (
                        eligibleRovers.map(r => (
                          <MenuItem key={r.id} value={r.id}>{roverLabel(r)}</MenuItem>
                        ))
                      )}
                    </Select>
                  </FormControl>
                )}
              </>
            )}
            {(assignedToType === 'rover'
              ? (selectedRoverId ? (user?.businessUnit || '') : '')
              : assignedToType === 'jobsite' ? assignedToName.trim() : requestRegion) && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  This license will be billed to: <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>{
                    assignedToType === 'rover'
                      ? (user?.businessUnit || '')
                      : assignedToType === 'jobsite' ? assignedToName.trim() : requestRegion
                  }</Box>
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                  Reserve only for expected use to avoid unnecessary charges.
                </Typography>
              </Box>
            )}
            <Typography variant="caption" color="text.secondary">
              NTRIP credentials are auto-generated and shown on the next step.
            </Typography>
            {requestError && <Typography color="error" variant="body2">{requestError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={advancedMode} onChange={(e) => { setAdvancedMode(e.target.checked); if (!e.target.checked) setAdvancedProvider(''); }} />}
            label={<Typography variant="body2" color="text.secondary">Advanced</Typography>}
            sx={{ m: 0 }}
          />
          <Box>
            <Button onClick={() => setRequestOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                requestLoading ||
                (advancedMode ? !advancedProvider : !requestLocation.trim()) ||
                !selectedDuration ||
                (selectedDuration === 'custom' && !customDate) ||
                (user?.role === 'bu_manager' && assignedToType === 'person' && (!requestRegion || !assignedToName.trim())) ||
                (user?.role === 'bu_manager' && assignedToType === 'jobsite' && !assignedToName.trim()) ||
                (user?.role === 'bu_manager' && assignedToType === 'rover' && !selectedRoverId)
              }
              onClick={handleRequestSubmit}
            >
              {requestLoading ? 'Checking...' : (advancedMode && advancedProvider ? 'Request' : 'Next')}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ══ REQUEST DIALOG — Step 2: Credentials ═════════════════════════ */}
      <Dialog open={requestOpen && requestStep === 2} onClose={handleRequestClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Connection Details
          <IconButton onClick={handleRequestClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {credentials && [
              { label: 'Address', value: credentials.server },
              { label: 'Port', value: credentials.port },
              { label: 'Username', value: credentials.username },
              { label: 'Password', value: credentials.password },
              { label: 'Mountpoint', value: credentials.mountpoint },
            ].map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleRequestClose}>OK</Button>
        </DialogActions>
      </Dialog>

      {/* ══ REQUEST DIALOG — Step 3: No Coverage ═════════════════════════ */}
      <Dialog open={requestOpen && requestStep === 3} onClose={handleRequestClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ position: 'relative', textAlign: 'center', pl: 6, pr: 6, color: '#ff1a1a', fontWeight: 700 }}>
          No Coverage Available
          <IconButton onClick={handleRequestClose} size="small" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 1 }}>
            <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.05rem' }}>
              For additional support, Please contact:
              <Box component="span" sx={{ display: 'block', color: 'primary.main', fontWeight: 800, mt: 0.5 }}>
                Mathieu Kippelen
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, p: 1.5, borderRadius: 1, backgroundColor: 'action.hover', width: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Email</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>mathieu.kippelen@menardusa.com</Typography>
                <IconButton size="small" onClick={copyEmail} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                  {emailCopied ? <CheckIcon fontSize="small" sx={{ color: '#00c853' }} /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleRequestClose}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ══ EXTEND DIALOG ════════════════════════════════════════════════ */}
      <Dialog open={extendOpen} onClose={() => setExtendOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Extend License
          <IconButton onClick={() => setExtendOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {extendCheckout && (
              <Typography variant="body2" color="text.secondary">
                Current Expiration: {formatExpiry(extendCheckout.expires_at)}
              </Typography>
            )}
            <TextField
              label="New Expiration Date"
              type="date"
              size="small"
              fullWidth
              value={extendDate}
              onChange={(e) => setExtendDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
              inputProps={{ min: (() => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; })() }}
            />
            {extendError && <Typography color="error" variant="body2">{extendError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setExtendOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleExtend} disabled={extending || !extendDate}>
            {extending ? 'Saving...' : 'Extend'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ MACHINE REQUEST DIALOG — Step 1 ══════════════════════════════ */}
      <Dialog open={machineReqOpen && machineReqStep === 1} onClose={() => setMachineReqOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Request Machine License
          <IconButton onClick={() => setMachineReqOpen(false)} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth size="small">
              <InputLabel>GPS System</InputLabel>
              <Select value={machineReqGps} label="GPS System" onChange={(e) => handleMachineReqGpsChange(e.target.value)}>
                <MenuItem value=""><em>Select GPS system</em></MenuItem>
                {gpsDetails.filter(g => !g.linked_license_id).map(g => (
                  <MenuItem key={g.id} value={String(g.id)}>
                    {g.gps_number}{g.machine_assignment ? ` — ${g.machine_assignment}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {machineReqGps && machineReqGpsMissing.length > 0 && (
              <Typography variant="body2" sx={{ color: '#4a90e2' }}>
                This GPS system is missing its {machineReqGpsMissing.join(' and ')} — filling {machineReqGpsMissing.length > 1 ? 'them' : 'it'} in below will update the GPS system.
              </Typography>
            )}
            {machineReqAdvancedMode ? (
              <FormControl fullWidth size="small">
                <InputLabel>NTRIP Provider</InputLabel>
                <Select
                  value={machineReqAdvancedProvider}
                  label="NTRIP Provider"
                  onChange={(e) => setMachineReqAdvancedProvider(e.target.value)}
                >
                  <MenuItem value="pointone">PointOneNav</MenuItem>
                  <MenuItem value="rtkpremium">Premium RTK</MenuItem>
                </Select>
              </FormControl>
            ) : (
              <TextField
                label="Location"
                size="small"
                fullWidth
                value={machineReqLocation}
                onChange={(e) => setMachineReqLocation(e.target.value)}
                placeholder="e.g. Toronto, ON  or  43.5927, -79.6463"
              />
            )}
            <FormControl fullWidth size="small">
              <InputLabel>Business Unit</InputLabel>
              <Select value={machineReqBizUnit} label="Business Unit" onChange={(e) => handleMachineReqBizUnitChange(e.target.value)}>
                <MenuItem value=""><em>Select business unit</em></MenuItem>
                {machineReqBizUnits.map(bu => <MenuItem key={bu} value={bu}>{bu}</MenuItem>)}
              </Select>
            </FormControl>
            <FormControl fullWidth size="small">
              <InputLabel>Region</InputLabel>
              <Select value={machineReqRegion} label="Region" onChange={(e) => setMachineReqRegion(e.target.value)}>
                <MenuItem value=""><em>Select region</em></MenuItem>
                {[...machineReqRegions, ...(!machineReqRegions.includes('General') ? ['General'] : [])].map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </Select>
            </FormControl>
            {machineReqError && <Typography color="error" variant="body2">{machineReqError}</Typography>}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2, justifyContent: 'space-between' }}>
          <FormControlLabel
            control={<Checkbox size="small" checked={machineReqAdvancedMode} onChange={(e) => { setMachineReqAdvancedMode(e.target.checked); if (!e.target.checked) setMachineReqAdvancedProvider(''); }} />}
            label={<Typography variant="body2" color="text.secondary">Advanced</Typography>}
            sx={{ m: 0 }}
          />
          <Box>
            <Button onClick={() => setMachineReqOpen(false)}>Cancel</Button>
            <Button
              variant="contained"
              disabled={
                machineReqLoading ||
                !machineReqGps ||
                (machineReqAdvancedMode ? !machineReqAdvancedProvider : !machineReqLocation.trim()) ||
                !machineReqBizUnit ||
                !machineReqRegion
              }
              onClick={handleMachineReqSubmit}
            >
              {machineReqLoading ? 'Requesting...' : 'OK'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* ══ MACHINE REQUEST DIALOG — Step 2: Credentials ═════════════════ */}
      <Dialog open={machineReqOpen && machineReqStep === 2} onClose={handleMachineReqClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          Connection Details
          <IconButton onClick={handleMachineReqClose} size="small"><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mt: 1 }}>
            {machineReqCredentials && [
              { label: 'Address', value: machineReqCredentials.server },
              { label: 'Port', value: machineReqCredentials.port },
              { label: 'Username', value: machineReqCredentials.username },
              { label: 'Password', value: machineReqCredentials.password },
              { label: 'Mountpoint', value: machineReqCredentials.mountpoint },
            ].filter(f => f.value).map(({ label, value }) => (
              <Box key={label} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1.5, borderRadius: 1, backgroundColor: 'action.hover' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</Typography>
                  <Typography variant="body2" fontWeight={600}>{value}</Typography>
                </Box>
                <IconButton size="small" onClick={() => navigator.clipboard.writeText(value)} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                  <CopyIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleMachineReqClose}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ══ MACHINE REQUEST DIALOG — Step 3: No Coverage ═════════════════ */}
      <Dialog open={machineReqOpen && machineReqStep === 3} onClose={handleMachineReqClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ position: 'relative', textAlign: 'center', pl: 6, pr: 6, color: '#ff1a1a', fontWeight: 700 }}>
          No Coverage Available
          <IconButton onClick={handleMachineReqClose} size="small" sx={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 1 }}>
            <Typography variant="body1" sx={{ textAlign: 'center', fontSize: '1.05rem' }}>
              For additional support, Please contact:
              <Box component="span" sx={{ display: 'block', color: 'primary.main', fontWeight: 800, mt: 0.5 }}>
                Mathieu Kippelen
              </Box>
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, p: 1.5, borderRadius: 1, backgroundColor: 'action.hover', width: '100%' }}>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Email</Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="body2" fontWeight={600}>mathieu.kippelen@menardusa.com</Typography>
                <IconButton size="small" onClick={copyEmail} sx={{ opacity: 0.6, '&:hover': { opacity: 1 } }}>
                  {emailCopied ? <CheckIcon fontSize="small" sx={{ color: '#00c853' }} /> : <CopyIcon fontSize="small" />}
                </IconButton>
              </Box>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button variant="contained" onClick={handleMachineReqClose}>Done</Button>
        </DialogActions>
      </Dialog>

      {/* ══ RTK DECOMMISSION DIALOG ══════════════════════════════════════ */}
      <Dialog open={rtkDecommissionOpen} onClose={() => setRtkDecommissionOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Return License</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Select the license to return.</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Device</InputLabel>
            <Select value={rtkDecommissionDevice} label="Device" onChange={(e) => setRtkDecommissionDevice(e.target.value)}>
              {filteredDevices.map((d) => {
                const gpsNum = licenseToGps[d.id] || licenseToGps[String(d.id)];
                return (
                  <MenuItem key={d.id} value={d.id}>
                    {gpsNum}{d.machine_name ? ` — ${d.machine_name}` : ''}
                  </MenuItem>
                );
              })}
            </Select>
          </FormControl>
          {rtkDecommissionError && <Typography color="error" variant="body2" sx={{ mt: 1 }}>{rtkDecommissionError}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRtkDecommissionOpen(false)}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleRtkDecommission} disabled={!rtkDecommissionDevice || rtkDecommissioning}>
            {rtkDecommissioning ? 'Returning...' : 'Return'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ══ RESET DIALOG (Point One Nav — commented out) ══════════════════
      <Dialog open={resetOpen} onClose={() => setResetOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reset License</DialogTitle>
        <DialogContent>
          <Typography sx={{ mb: 2 }}>Select the license you wish to reset</Typography>
          <FormControl fullWidth size="small">
            <InputLabel>Device</InputLabel>
            <Select value={resetDevice} label="Device" onChange={(e) => setResetDevice(e.target.value)}>
              {devices.map((d) => (
                <MenuItem key={d.id} value={d.label}>{d.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={() => setResetOpen(false)}>OK</Button>
        </DialogActions>
      </Dialog>
      ══ END RESET DIALOG ══════════════════════════════════════════════ */}

    </Box>
  );
}

export default GPSLicenses;
